// THE ROADMAP — the deliverables in time, and the workbenches that work them out.
//
// Prototyped with Yves (artifact "Roadmap Workbench") and refined 2026-08-24/25:
//   · week-grid timeline, standard lane height; cards drag smoothly and snap gently to week
//     lines (writes start_date/due_date); pulling either edge stretches; GRABBING THE EMPTY
//     GRID pans the timeline laterally;
//   · clicking a card slides a workbench UP from below into THE DOCK — a fixed strip between
//     the sidebar and the right edge. A second click on another card opens a second bench
//     BESIDE the first, a third beside that, as many as the width holds (a wide monitor holds
//     more); past that, the oldest benches FOLD into a narrow rail at the dock's right edge —
//     a tab each, × to close or click to unfold — and unfold by themselves when the width
//     returns. Nothing is killed by a narrower window. The roadmap never disappears behind the
//     dock: the page gains the dock's height at the bottom, so the map scrolls up above it,
//     and opening a bench glides the page so the clicked card stays in view;
//   · each workbench: name, description and definition of done edit ON DEMAND (✎), the
//     CONTENT ITEMS (✎ on the title, accordion: spec, due date, documents, requirement
//     links, provenance) date-sorted and reorderable; the requirements with their SOURCE
//     SPANS one click away;
//   · "Refine with the agent": conversations survive closing the bench (per deliverable, for
//     the session) and a History tab lists every conversation this engagement ever had, read
//     straight from the run transcripts. The agent proposes (propose_deliverable_item), the
//     operator holds — P1 throughout.
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Tx } from '../../components/Tx.js';

import { agentApi, type ConversationResult, type Proposal, type RunRow, type TranscriptMessage, type TurnActivity } from '../../api/agent.js';
import AgentFlyout, { type FlyoutMsg } from '../../components/AgentFlyout.js';
import {
  deliverablesApi, registerApi,
  type Deliverable, type DeliverableItem, type DeliverableLink, type ProseReference, type RegisterRow,
  type TetherQuestion, type WorkingOverride,
} from '../../api/register.js';
import { proposalDeliverableRef, proposalSubject } from '../../../shared/proposalSubject.js';
import { HOLDABLE_IN_THREAD } from './threadHold.js';
import RefSuggest, { type RefOption } from '../../components/RefSuggest.js';
import { REF_RE } from './refLink.js';
import { documentsApi, type DocumentRow } from '../../api/documents.js';
import { useToast } from '../../components/Toast.js';
import { useLocale, useT } from '../../i18n/index.js';
import { useEngagement } from '../../context/EngagementContext.js';
import { ROUTES, useOpenRoute } from '../../routes.js';
import './RoadmapView.de.js';
import './RoadmapView.fr.js';
import { fitWeekPx, layoutCards } from './roadmapLayout.js';

const WEEKS_PER_MONTH = 4;
const SNAP_PX = 11;
/** A bench narrower than this cannot show its work legibly — the dock holds as many as fit. */
const BENCH_MIN_W = 600;
const DOCK_GAP = 14;
const DOCK_INSET = 28;
/** The fold rail at the dock's right edge — the tabs of the benches that do not fit. */
const FOLD_W = 40;
/** A stable empty array — a fresh `[]` every render would defeat nothing here, but this is
 *  clearer about intent than a literal at the call site. */
const EMPTY_PROPOSALS: Proposal[] = [];

interface YM { y: number; m: number }
const dim = (y: number, m: number): number => new Date(y, m, 0).getDate();
const parseIso = (s: string): { y: number; m: number; d: number } => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return { y, m, d };
};
const iso = (y: number, m: number, d: number): string =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** A proposal card in a thread: filed live by the turn, or read back off the run it belongs to. */
type ChatProposal = ConversationResult['proposals'][number];
// What a thread card offers Hold for now lives in threadHold.ts — the planner's thread
// (PlannerChat.tsx) reads the same set, so the two surfaces cannot drift apart.
interface ChatMsg { who: 'op' | 'agent'; text: string; proposals?: ChatProposal[] }
/** Bench conversations survive closing the bench for the life of the SPA — keyed per deliverable. */
const chatCache = new Map<number, { msgs: ChatMsg[]; runId: number | null }>();
/** The chat panel's fold state — the same persistence the board's ⤢ breakout uses. */
const CHAT_KEY = 'signet.rw.chat';
const readChatOpen = (): boolean => {
  try { return localStorage.getItem(CHAT_KEY) !== '0'; } catch { return true; }   // private mode
};
/** Under this bench width the chat cannot hold a column of its own — it overlays (see main.css). */
const BENCH_NARROW = 940;

const transcriptText = (m: TranscriptMessage): string =>
  (m.parts ?? []).map((p) => String((p as { text?: unknown }).text ?? '')).filter(Boolean).join('\n');

/** The dock's height, as the stylesheet computes it — needed before the dock exists, to glide the page. */
const dockHeight = (): number => {
  const h = window.innerHeight;
  return Math.max(340, Math.min(0.54 * h, h - 330));
};
/** How many of `count` open benches the dock can show side by side at the current content
 *  width — when not all fit, the fold rail takes its width first. */
const dockCapacity = (count: number): number => {
  const content = document.querySelector<HTMLElement>('.shell .content');
  const w = (content?.getBoundingClientRect().width ?? window.innerWidth) - DOCK_INSET * 2;
  const fits = (avail: number) => Math.max(1, Math.floor((avail + DOCK_GAP) / (BENCH_MIN_W + DOCK_GAP)));
  const cap = fits(w);
  return count <= cap ? cap : fits(w - FOLD_W - DOCK_GAP);
};

/**
 * An editor that grows with its text — no resize handles. Plain text with markdown-lite
 * (**fett**, *kursiv*, "- " bullets) that <Rich> renders; STORED AS TEXT, so every other
 * surface (list view, SOW render, workspace projections) keeps reading it safely.
 */
function AutoGrow({ value, placeholder, onDone, refOptions }: {
  value: string; placeholder: string; onDone: (v: string) => void;
  /** When given, # summons the reference picker (components/RefSuggest.tsx). */
  refOptions?: RefOption[];
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.focus(); el.selectionStart = el.value.length; el.style.height = 'auto'; el.style.height = `${el.scrollHeight + 2}px`; }
  }, []);
  return (
    <div className="refwrap">
      <textarea
        ref={ref}
        className="rw-grow"
        defaultValue={value}
        placeholder={placeholder}
        onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${el.scrollHeight + 2}px`; }}
        onBlur={(e) => onDone(e.target.value)}
      />
      {refOptions ? <RefSuggest taRef={ref} options={refOptions} /> : null}
    </div>
  );
}

/** Refs written into prose render as ref chips — recovered from the text (refLink.ts), never stored. */
function linkifyRefs(sTxt: string, k: () => number): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of sTxt.matchAll(new RegExp(REF_RE.source, 'g'))) {
    if (m.index! > last) out.push(sTxt.slice(last, m.index));
    out.push(<span className="src-ref ref-inline" key={k()}>{m[0]}</span>);
    last = m.index! + m[0].length;
  }
  if (last < sTxt.length) out.push(sTxt.slice(last));
  return out;
}

function inlineMd(sTxt: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = sTxt, kc = 0;
  const k = () => kc++;
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/;
  while (rest) {
    const m = re.exec(rest);
    if (!m) { out.push(...linkifyRefs(rest, k)); break; }
    if (m.index > 0) out.push(...linkifyRefs(rest.slice(0, m.index), k));
    out.push(m[1] !== undefined ? <b key={k()}>{linkifyRefs(m[1], k)}</b> : <i key={k()}>{linkifyRefs(m[2], k)}</i>);
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

/** markdown-lite display: paragraphs, "- " bullets, **fett**, *kursiv*. */
function Rich({ text, className }: { text: string | null; className?: string }) {
  if (!text || !text.trim()) return null;
  const blocks: ReactNode[] = [];
  let list: string[] = [], k = 0;
  const flush = () => {
    if (list.length) { blocks.push(<ul key={k++}>{list.map((li, i) => <li key={i}>{inlineMd(li)}</li>)}</ul>); list = []; }
  };
  for (const ln of text.split('\n')) {
    if (/^\s*[-•]\s+/.test(ln)) list.push(ln.replace(/^\s*[-•]\s+/, ''));
    else { flush(); if (ln.trim()) blocks.push(<p key={k++}>{inlineMd(ln)}</p>); }
  }
  flush();
  return <div className={className}>{blocks}</div>;
}

/** A translated sentence whose `{placeholders}` are React nodes (a styled ref) — translated
 *  with the placeholders intact (t() without vars), then split around them. */
function nodes(text: string, parts: Record<string, ReactNode>): ReactNode[] {
  return text.split(/(\{\w+\})/).map((seg, i) => {
    const m = /^\{(\w+)\}$/.exec(seg);
    return m && m[1] in parts ? <Fragment key={i}>{parts[m[1]]}</Fragment> : seg;
  });
}

/** THE PENCIL LAYER: which of the two pictures the board is drawing. */
type ViewMode = 'record' | 'assume';
const VIEW_KEY = 'signet.roadmap.view';
const readViewMode = (): ViewMode | null => {
  try { const v = localStorage.getItem(VIEW_KEY); return v === 'record' || v === 'assume' ? v : null; }
  catch { return null; }   // private mode
};

/** The assumption standing on one field of one row, if there is one. */
const overrideOf = (rows: WorkingOverride[] | undefined, field: 'due_date' | 'start_date'): WorkingOverride | null =>
  (rows ?? []).find((o) => o.field === field) ?? null;

// ═══════════════════════════════════════════════════════════════════════════════════════
// THE STANDING HALF — the same pencil layer generalised from "what date" to "does it
// survive" (docs/ATTEST.md; working_overrides.entity_type/field already carry 'requirement'
// and 'standing' — schema.sql, server/utils/workingOverrides.ts, server/routes/register.ts).
// src/client/api/register.ts's WorkingOverride TYPE predates the generalisation (entity_type
// 'deliverable'|'deliverable_item' only, field due_date/start_date only) — that file is
// another lane's, so this is a LOCAL WIDENING, not a new endpoint: every call below still goes
// through registerApi.overrides / .setOverride / .revertOverride, the same three routes the
// date pencil already uses, just read and written through a shape that matches what the
// server already sends. Fold entity_type 'requirement' and field 'standing' into the shared
// WorkingOverride type when that file's lane picks this up, and this block collapses to it.
// ═══════════════════════════════════════════════════════════════════════════════════════
interface StandingOverride {
  id: number;
  engagement_id: number;
  entity_type: 'deliverable' | 'requirement';
  entity_id: number;
  entity_ref: string | null;
  entity_label?: string | null;
  field: 'standing';
  value: 'provisional';
  note: string | null;
  open_question_id: number;
  question_ref: string | null;
  question: string;
  question_status: string;
  created_at: string;
}
/** The standing override on one row's own `overrides` array, if any (deliverable payloads
 *  already embed it — server/routes/deliverables.ts's per-deliverable subquery has no `field`
 *  filter, so it rides along beside the date overrides without any extra fetch). */
const standingOf = (rows: WorkingOverride[] | undefined): StandingOverride | null => {
  const arr = (rows ?? []) as unknown as StandingOverride[];
  return arr.find((o) => o.field === 'standing') ?? null;
};
/** PUT /register/overrides, widened past the client contract's date-only union (see above). */
const setStanding = (
  engagementId: number,
  body: { entity_type: 'deliverable' | 'requirement'; entity_id: number; open_question_id: number; note?: string | null },
): Promise<StandingOverride> =>
  registerApi.setOverride(engagementId, { ...body, field: 'standing', value: 'provisional' } as unknown as Parameters<
    typeof registerApi.setOverride
  >[1]) as unknown as Promise<StandingOverride>;

const HINT = 'saves when you leave the field · **fett** · *kursiv* · „- “ für Listen · # inserts a reference';
/** Short month names — catalogue keys; the German catalogue renders März, Mai, Juni… */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ═══════════════════════════════════════════════════════════════════════════════════════
// THE PENCIL — "als Annahme planen". A date the operator wants to PLAN with while the ground
// under it is still being argued about. It writes `working_overrides` and NOTHING else: the
// row it shadows keeps its own date until the client call adopts the assumption explicitly
// (P1 — the operator proposes to themselves; only the call commits).
//
// The tether is required, not decorative. An assumption hangs on the OPEN QUESTION that has to
// settle before it can become the record — so when Q-003 settles, this date knows whether to
// die or to land. The obvious tethers (the open questions that name a requirement this
// deliverable satisfies) are computed server-side and offered first; any open question of the
// engagement is still reachable, because the obvious one is not always the right one.
// ═══════════════════════════════════════════════════════════════════════════════════════
function Pencil({
  engagementId, entityType, entityId, field, recordValue, existing, preferred, all, onDone, onCancel,
}: {
  engagementId: number;
  entityType: 'deliverable' | 'deliverable_item';
  entityId: number;
  field: 'due_date' | 'start_date';
  /** What the record says today — the pencil opens on it, so a plan starts from the truth. */
  recordValue: string | null;
  existing: WorkingOverride | null;
  preferred: TetherQuestion[];
  all: TetherQuestion[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const preferredIds = new Set(preferred.map((q) => q.id));
  const others = all.filter((q) => !preferredIds.has(q.id));
  const [qId, setQId] = useState<number>(existing?.open_question_id ?? preferred[0]?.id ?? all[0]?.id ?? 0);
  const [value, setValue] = useState<string>(existing?.value ?? recordValue ?? '');
  const [note, setNote] = useState<string>(existing?.note ?? '');
  const [saving, setSaving] = useState(false);

  const label = (q: TetherQuestion): string =>
    `${q.ref ?? '—'} · ${q.question.replace(/\s+/g, ' ').slice(0, 72)}`;

  const save = async () => {
    if (!qId || !value) return;
    setSaving(true);
    try {
      await registerApi.setOverride(engagementId, {
        entity_type: entityType, entity_id: entityId, field, value,
        open_question_id: qId, note: note.trim() || null,
      });
      onDone();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The assumption was refused.'));
      setSaving(false);
    }
  };

  if (!all.length) {
    return (
      <div className="rw-pencil">
        <span className="rw-none">{t('Nothing open to hang an assumption on — a pencilled date has to say what it waits on.')}</span>
        <button type="button" className="lnkb" onClick={onCancel}>{t('Cancel')}</button>
      </div>
    );
  }
  return (
    <div className="rw-pencil">
      <span className="u-label">{t('plan as an assumption')}</span>
      <select
        value={qId}
        aria-label={t('The open question this assumption waits on')}
        onChange={(e) => setQId(Number(e.target.value))}
      >
        {preferred.map((q) => <option key={q.id} value={q.id}>{label(q)}</option>)}
        {others.length ? (
          <optgroup label={t('every other open question')}>
            {others.map((q) => <option key={q.id} value={q.id}>{label(q)}</option>)}
          </optgroup>
        ) : null}
      </select>
      <input type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label={t('The date you are assuming')} />
      <input
        className="nt"
        value={note}
        placeholder={t('why, in one line — optional')}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="button" className="btn btn-sm btn-accent" disabled={saving || !value || !qId} onClick={() => void save()}>
        {t('Pencil it in')}
      </button>
      <button type="button" className="lnkb" onClick={onCancel}>{t('Cancel')}</button>
    </div>
  );
}

/** One standing assumption, said in full: the mark, the date, and the question it waits on. */
function GhostLine({ o, onRevert }: { o: WorkingOverride; onRevert: () => void }) {
  const { t, fmtDate } = useLocale();
  return (
    <span
      className="rw-ghostline"
      title={t('Assumption — waiting on {ref}: {question}', { ref: o.question_ref ?? '—', question: o.question })}
    >
      <span className="att att--proposed" aria-hidden="true" />
      <span className="tag-unheld">{t('Assumption')}</span>
      <span className="voice-figure">{fmtDate(o.value)}</span>
      <span className="src-ref">{o.question_ref ?? ''}</span>
      {o.note ? <span className="nt">{o.note}</span> : null}
      <button type="button" className="x" title={t('Rub this assumption out — the record never moved')} onClick={onRevert}>×</button>
    </span>
  );
}

/**
 * One standing mark, said in full: PROVISIONAL, the question it is tethered to (a ref that
 * LEADS to the question — an untethered mark is the sticky note this whole layer exists to
 * prevent, so the ref is never just printed), and a way to clear it once it has been treated.
 */
function StandingLine({ o, onRevert, onGoToQuestion }: { o: StandingOverride; onRevert: () => void; onGoToQuestion: (questionId: number) => void }) {
  const { t } = useLocale();
  return (
    <span
      className="rw-standing"
      title={t('Provisional — waiting on {ref}: {question}', { ref: o.question_ref ?? '—', question: o.question })}
    >
      <span className="att att--standing" aria-hidden="true" />
      <span className="tag-unheld">{t('Provisional')}</span>
      <button type="button" className="src-ref rw-qlink" onClick={() => onGoToQuestion(o.open_question_id)}>
        {o.question_ref ?? '—'}
      </button>
      {o.note ? <span className="nt">{o.note}</span> : null}
      <button type="button" className="x" title={t('Clear this mark — the record never moved')} onClick={onRevert}>×</button>
    </span>
  );
}

/**
 * SETTING a standing mark. Same tether discipline as the date pencil: refuses to save with no
 * question chosen, and offers only the OPEN questions RoadmapView already loaded (`openQs`) —
 * no free-text "reason" ever substitutes for the tether (that is exactly the untethered sticky
 * note the pencil layer was built to prevent).
 */
function StandingPicker({
  engagementId, entityType, entityId, all, onDone, onCancel,
}: {
  engagementId: number;
  entityType: 'deliverable' | 'requirement';
  entityId: number;
  all: TetherQuestion[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [qId, setQId] = useState<number>(all[0]?.id ?? 0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const label = (q: TetherQuestion): string =>
    `${q.ref ?? '—'} · ${q.question.replace(/\s+/g, ' ').slice(0, 72)}`;

  const save = async () => {
    if (!qId) return;
    setSaving(true);
    try {
      await setStanding(engagementId, { entity_type: entityType, entity_id: entityId, open_question_id: qId, note: note.trim() || null });
      onDone();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The mark was refused.'));
      setSaving(false);
    }
  };

  if (!all.length) {
    return (
      <div className="rw-pencil">
        <span className="rw-none">{t('Nothing open to tether a provisional mark to — mark one only once a question is open to settle it.')}</span>
        <button type="button" className="lnkb" onClick={onCancel}>{t('Cancel')}</button>
      </div>
    );
  }
  return (
    <div className="rw-pencil">
      <span className="u-label">{t('mark provisional')}</span>
      <select value={qId} aria-label={t('The open question this mark waits on')} onChange={(e) => setQId(Number(e.target.value))}>
        {all.map((q) => <option key={q.id} value={q.id}>{label(q)}</option>)}
      </select>
      <input
        className="nt"
        value={note}
        placeholder={t('why, in one line — optional')}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="button" className="btn btn-sm btn-accent" disabled={saving || !qId} onClick={() => void save()}>
        {t('Mark provisional ◈')}
      </button>
      <button type="button" className="lnkb" onClick={onCancel}>{t('Cancel')}</button>
    </div>
  );
}

/**
 * The claim-marks a card wears — ◇ untreated, ◈ provisional, "linked content changed" —
 * shared by the grid's cards and the tray's card-shaped chips so the two can never drift.
 */
function CardMarks({ del, untreated }: { del: Deliverable; untreated: Proposal[] }) {
  const { t, tp } = useLocale();
  const standing = standingOf(del.overrides);
  const stale = (del.links ?? []).some((l) => l.stale);
  if (!untreated.length && !standing && !stale) return null;
  return (
    <div className="rw-marks">
      {untreated.length ? (
        <span
          className="rw-untreated"
          title={tp(
            untreated.length,
            '{n} proposal from the agent is still waiting on a Hold or a decision',
            '{n} proposals from the agent are still waiting on a Hold or a decision',
            { n: untreated.length },
          )}
        >
          <span className="att att--proposed" aria-hidden="true" />
          <span className="tag-unheld">{tp(untreated.length, '{n} untreated', '{n} untreated')}</span>
        </span>
      ) : null}
      {standing ? (
        <span
          className="rw-standing"
          title={t('Provisional — waiting on {ref}: {question}', {
            ref: standing.question_ref ?? '—', question: standing.question,
          })}
        >
          <span className="att att--standing" aria-hidden="true" />
          <span className="tag-unheld">{t('Provisional')}</span>
          <span className="src-ref">{standing.question_ref ?? ''}</span>
        </span>
      ) : null}
      {stale ? (
        <span
          className="rw-stale"
          title={t('Content this deliverable builds on has changed — open the bench to reconcile')}
        >
          <span className="att att--proposed" aria-hidden="true" />
          <span className="tag-unheld">{t('linked content changed')}</span>
        </span>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// ONE WORKBENCH — one deliverable worked out. Owns its own items, edit state and
// conversation, so several can stand side by side in the dock without sharing anything.
// ═══════════════════════════════════════════════════════════════════════════════════════
function Workbench({
  engagementId, del, docs, rowOf, onOpenSpan, onReload, onClose, onStep, hasPrev, hasNext, patchDeliverable, flash,
  openQuestions, onOverrideChange, requirementStandings, untreatedProposals, untreatedByReq, onGoToQuestion, onStandingsChange, onProposalChange,
  allDels, onOpenBench, refOptions,
}: {
  engagementId: number;
  del: Deliverable;
  docs: DocumentRow[];
  /** Every deliverable of the board — the linker's target picker, and the ref → id jump. */
  allDels: Deliverable[];
  /** Open (or surface) another deliverable's bench — a link chip leads to its other end. */
  onOpenBench: (deliverableId: number) => void;
  /** What # can reference in this bench's editors — deliverables, requirements, documents. */
  refOptions: RefOption[];
  /** Every OPEN question of the engagement — the pencil's fallback tether list, and the ONLY
   *  list a provisional mark may be tethered to (no per-entity "preferred" is computed for
   *  standing — the deliverable's own open_questions stays date-pencil-only). */
  openQuestions: TetherQuestion[];
  /** An assumption was written or rubbed out: the board re-reads this deliverable. */
  onOverrideChange: (deliverableId: number) => void;
  /** The standing mark on each of THIS deliverable's requirements, keyed by requirement id —
   *  computed once, engagement-wide, at the board (a requirement carries no `overrides` of its
   *  own on the wire yet; see the board's `standings` fetch). */
  requirementStandings: Map<number, StandingOverride>;
  /** Pending proposals that name THIS deliverable directly — ◇ UNTREATED. */
  untreatedProposals: Proposal[];
  /** The same, keyed by requirement id instead — the Satisfies list's own ◇ UNTREATED. */
  untreatedByReq: Map<number, Proposal[]>;
  /** Lead to the open question a mark is tethered to. */
  onGoToQuestion: (questionId: number) => void;
  /** A REQUIREMENT's standing mark was set or cleared: the board re-reads the engagement-wide list. */
  onStandingsChange: () => void;
  /** A pending proposal was held (or dropped): the board re-reads the untreated queue. */
  onProposalChange: () => void;
  rowOf: (reqId: number) => RegisterRow | null;
  onOpenSpan: (requirementId: number, docRef: string) => void;
  onReload: () => void;
  onClose: () => void;
  onStep: (dir: -1 | 1) => void;
  hasPrev: boolean;
  hasNext: boolean;
  patchDeliverable: (id: number, body: Parameters<typeof deliverablesApi.update>[1], word?: string) => Promise<void>;
  /** Bumped by the board when the operator clicks a card whose bench is already open — the bench glows. */
  flash: number;
}) {
  const toast = useToast();
  const { t, tp, fmtDate } = useLocale();
  const [items, setItems] = useState<DeliverableItem[]>([]);
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [newItem, setNewItem] = useState('');
  /** Which block is in edit mode — texts display rendered until the ✎ is clicked. */
  const [editing, setEditing] = useState<string | null>(null);

  /** Which pencil is open: `del-due_date` | `del-start_date` | `item-<id>`. One at a time. */
  const [pencil, setPencil] = useState<string | null>(null);

  const cached = chatCache.get(del.id);
  const [chat, setChat] = useState<ChatMsg[]>(cached?.msgs ?? []);
  const [chatRunId, setChatRunId] = useState<number | null>(cached?.runId ?? null);
  const [ask, setAsk] = useState('');
  const [asking, setAsking] = useState(false);
  /** Set when this mount did not start the turn itself (the bench was closed and reopened
   *  while the agent was answering) — the poll below reloads the reply from the record. */
  const [watching, setWatching] = useState(false);
  /** True while THIS mount's blocking POST is in flight — the re-attach probe must never
   *  mistake an owned turn for an orphaned one (its resolve can land after sendAsk started). */
  const ownedTurn = useRef(false);
  /** Set by "+ New conversation": the probe's run DISCOVERY stands down, or it would find the
   *  just-dismissed run still answering and re-attach the thread the operator walked away from. */
  const freshStart = useRef(false);
  /** The live doing behind a blocking turn — polled while `asking`; null shows clock only. */
  const [turnAct, setTurnAct] = useState<TurnActivity | null>(null);
  const [askStartedAt, setAskStartedAt] = useState(0);
  /** Signals into the shared flyout: focus the composer / snap the thread to its foot. */
  const [askFocus, setAskFocus] = useState(0);
  const [footSignal, setFootSignal] = useState(0);
  /** The conversation rail: this deliverable's threads, or every thread of the engagement. */
  const [railTab, setRailTab] = useState<'mine' | 'all'>('mine');
  const [convos, setConvos] = useState<RunRow[] | null>(null);
  /** Set when an older thread was reopened — the next ask re-primes the deliverable context. */
  const [resumed, setResumed] = useState(false);
  /** The chat panel: folded to a spine or out; and whether the conversation list is over it. */
  const [chatOpen, setChatOpen] = useState<boolean>(readChatOpen);
  const [convosOpen, setConvosOpen] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!flash) return;
    const el = rootRef.current; if (!el) return;
    el.classList.add('glow');
    const timer = setTimeout(() => el.classList.remove('glow'), 1500);
    return () => clearTimeout(timer);
  }, [flash]);

  /** Persisting at the call site, not in an effect: the width guard below must NOT write. */
  const setChatFold = useCallback((open: boolean) => {
    setChatOpen(open);
    try { localStorage.setItem(CHAT_KEY, open ? '1' : '0'); } catch { /* private mode */ }
  }, []);
  // A bench under BENCH_NARROW shows the chat as an overlay, so it opens FOLDED there — and
  // deliberately without touching storage: a phone-width session must not un-choose the desk's choice.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (el && el.getBoundingClientRect().width < BENCH_NARROW) setChatOpen(false);
  }, []);

  const loadItems = useCallback(async () => {
    try { setItems(await deliverablesApi.items(del.id)); }
    catch (err) { toast('err', err instanceof Error ? err.message : t('The items did not load.')); }
  }, [del.id, toast, t]);
  useEffect(() => { void loadItems(); }, [loadItems]);

  /** This bench's # vocabulary: the board's plus this deliverable's own content items. */
  const benchRefOptions = useMemo<RefOption[]>(() => [
    ...refOptions,
    ...items.filter((it) => it.ref).map((it) => ({ ref: it.ref!, label: it.title })),
  ], [refOptions, items]);

  /** WHO NAMES THIS REF — derived from the register's prose (utils/proseReferences.ts), so an
   *  edit in this bench is made with open eyes, and the chat preamble carries the same list. */
  const [refbacks, setRefbacks] = useState<ProseReference[]>([]);
  useEffect(() => {
    let live = true;
    deliverablesApi.references(engagementId, del.ref)
      .then((out) => { if (live) setRefbacks(out.references); })
      .catch(() => { if (live) setRefbacks([]); });
    return () => { live = false; };
  }, [engagementId, del.ref]);

  // ── items ──
  const saveItem = useCallback(async (itemId: number, body: Parameters<typeof deliverablesApi.updateItem>[1]) => {
    try { await deliverablesApi.updateItem(itemId, body); await loadItems(); }
    catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [loadItems, toast, t]);
  const addItem = useCallback(async () => {
    const title = newItem.trim();
    if (!title) return;
    try {
      await deliverablesApi.addItem(del.id, { title, provenance: 'operator' });
      setNewItem('');
      await loadItems();
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The item was refused.')); }
  }, [newItem, del.id, loadItems, toast, t]);
  const moveItem = useCallback(async (idx: number, dir: -1 | 1) => {
    const other = idx + dir;
    if (other < 0 || other >= items.length) return;
    const a = items[idx], b = items[other];
    await Promise.all([
      deliverablesApi.updateItem(a.id, { sort_order: b.sort_order === a.sort_order ? b.sort_order + dir : b.sort_order }),
      deliverablesApi.updateItem(b.id, { sort_order: a.sort_order }),
    ]).catch(() => null);
    await loadItems();
  }, [items, loadItems]);

  // ── the scoped conversation, cached per deliverable ──
  const setChatBoth = useCallback((updater: (c: ChatMsg[]) => ChatMsg[], runId?: number | null) => {
    setChat((c) => {
      const next = updater(c);
      chatCache.set(del.id, { msgs: next, runId: runId !== undefined ? runId : chatRunId });
      return next;
    });
    if (runId !== undefined) setChatRunId(runId);
  }, [del.id, chatRunId]);

  // context_ref is the recorded provenance; the prompt prefix stays as the fallback for every
  // conversation from before the column existed.
  const isMine = useCallback(
    (r: RunRow) => r.context_ref === del.ref || (r.prompt ?? '').startsWith(`[Deliverable refinement — ${del.ref}`),
    [del.ref],
  );
  /** Strip the machine preamble from a stored user turn — show what the operator typed. */
  const operatorWords = (raw: string): string => {
    const i = raw.lastIndexOf('Operator: ');
    return (i >= 0 ? raw.slice(i + 'Operator: '.length) : raw).trim();
  };
  const loadConvos = useCallback(async () => {
    try {
      const out = await agentApi.listRuns(engagementId);
      const cs = out.runs.filter((r) => r.mode === 'conversation');
      setConvos(cs);
      return cs;
    } catch { setConvos([]); return [] as RunRow[]; }
  }, [engagementId]);
  /** The run's thread as the RECORD has it — openConversation and the orphan-settle both read here. */
  const readThread = useCallback(async (rid: number): Promise<ChatMsg[]> => {
    const detail = await agentApi.getRun(rid);
    const msgs: ChatMsg[] = [];
    for (const m of detail.transcript ?? []) {
      const txt = transcriptText(m);
      if (!txt.trim()) continue;
      if (m.role === 'user') msgs.push({ who: 'op', text: operatorWords(txt) });
      else msgs.push({ who: 'agent', text: txt });
    }
    // THE PROPOSALS COME BACK WITH THE THREAD. `getRun` already returns everything the run
    // filed; the old code dropped it, so a reopened conversation showed the agent's words with
    // no cards and no Hold. They ride on the LAST agent turn — the same place, the same card and
    // the same button as a live turn, so a still-pending create_deliverable_item is holdable
    // from history exactly as it is live. (Run-level, not turn-level: the transcript carries no
    // trustworthy per-turn attribution, and inventing one would misplace them silently.)
    const filed: ChatProposal[] = (detail.proposals ?? []).map((pr) => ({
      id: pr.id, kind: pr.kind as ChatProposal['kind'], status: pr.status, payload: pr.payload,
    }));
    if (filed.length) {
      let last = -1;
      msgs.forEach((m, i) => { if (m.who === 'agent') last = i; });
      if (last >= 0) msgs[last] = { ...msgs[last], proposals: filed };
      else msgs.push({ who: 'agent', text: '', proposals: filed });   // proposals, no transcript
    }
    return msgs;
  }, []);
  const openConversation = useCallback(async (run: RunRow) => {
    try {
      const msgs = await readThread(run.id);
      freshStart.current = false;   // an explicitly reopened thread may re-attach again
      setChat(msgs);
      setChatRunId(run.id);
      setResumed(true);
      setFootSignal((n) => n + 1);
      setConvosOpen(false);
      chatCache.set(del.id, { msgs, runId: run.id });
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The transcript did not load.')); }
  }, [del.id, readThread, toast, t]);
  // The rail loads with the bench; if nothing is cached, the latest thread of THIS
  // deliverable is restored from the record — a reload loses nothing anymore.
  useEffect(() => {
    void loadConvos().then((cs) => {
      const cached = chatCache.get(del.id);
      if (cached?.msgs.length) return;
      // isMine, not a bare prompt-prefix match: a thread recorded by context_ref alone (the
      // rail's own filter since the column landed) must restore here too.
      const mine = cs.filter(isMine);
      if (mine.length) void openConversation(mine[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [del.id]);
  const newConversation = useCallback(() => {
    setChat([]); setChatRunId(null); setResumed(false);
    // Watching an ORPHANED turn ends here — otherwise the settle below would refill the
    // cleared thread with the old conversation a few seconds later, and the probe's discovery
    // would re-attach it. An OWNED turn keeps its busy flag: its blocking POST is still out.
    freshStart.current = true;
    setWatching(false);
    if (!ownedTurn.current) setAsking(false);
    chatCache.set(del.id, { msgs: [], runId: null });
  }, [del.id]);

  /** Escape closes the conversation list, like every other transient layer in the console. */
  useEffect(() => {
    if (!convosOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setConvosOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [convosOpen]);

  // A thread restored while its turn is STILL RUNNING re-attaches to it: closing the bench
  // killed the component that was showing "answering", not the answer — so probe the run
  // (or discover a first-turn run by this bench's context), bring the band back on the
  // turn's own clock, and let the poll below reload the reply from the record when it ends.
  // Without this, a reopened bench looked idle and a re-ask ran into "The agent is still
  // answering the previous message."
  useEffect(() => {
    if (asking) return;
    let live = true;
    const probe = async () => {
      let rid = chatRunId;
      if (rid === null) {
        if (freshStart.current) return;
        try {
          const out = await agentApi.listRuns(engagementId, 10);
          const mine = out.runs.find((r) => r.live && r.mode === 'conversation' && isMine(r));
          if (mine) rid = Number(mine.id);
        } catch { return; }
      }
      if (rid === null) return;
      try {
        const a = await agentApi.runActivity(rid);
        if (!live || !a.busy || ownedTurn.current) return;
        setChatRunId(rid);   // pinned — the settle below must not depend on re-discovery succeeding
        setAsking(true);
        setWatching(true);
        setAskStartedAt(a.since ?? Date.now());
        setTurnAct(a);
      } catch { /* quiet — a finished run answers {busy:false} anyway */ }
    };
    void probe();
    return () => { live = false; };
  }, [asking, chatRunId, engagementId, isMine]);

  /** The thread as rendered NOW — the settle below compares the record against it. */
  const chatRef = useRef(chat);
  useEffect(() => { chatRef.current = chat; });
  /** Settle attempts after the orphaned run went quiet — see the race note in the poll. */
  const settleTries = useRef(0);

  // While the (blocking) turn is in flight, poll what the agent is DOING. The first turn of
  // a fresh thread has no run id yet — the run is born inside the blocking call — so it is
  // discovered from the runs list by this bench's own context; until then the band shows the
  // clock alone, which is still more honest than "thinking…".
  useEffect(() => {
    if (!asking) { setTurnAct(null); return; }
    let live = true;
    const poll = async () => {
      let rid = chatRunId;
      if (rid === null) {
        try {
          const out = await agentApi.listRuns(engagementId, 10);
          const mine = out.runs.find((r) => r.live && r.mode === 'conversation' && isMine(r));
          if (mine) rid = Number(mine.id);
        } catch { /* the clock keeps ticking */ }
      }
      if (rid !== null) {
        try {
          const a = await agentApi.runActivity(rid);
          if (!live) return;
          setTurnAct(a.busy ? a : null);
          // Re-attached and the turn just ended: the blocking POST belonged to a bench that
          // no longer exists, so the reply is only in the record. The record is written a
          // beat AFTER the run goes quiet (busy clears before recordTranscript commits), so
          // accept the reload only once the transcript has ADVANCED past what is on screen —
          // otherwise keep watching and try again next tick, up to a bounded patience.
          if (!a.busy && watching) {
            const thread = await readThread(rid);
            if (!live) return;
            const settled = thread.length >= chatRef.current.length && thread[thread.length - 1]?.who === 'agent';
            if (settled || settleTries.current >= 8) {
              settleTries.current = 0;
              setWatching(false);
              setAsking(false);
              setChat(thread);
              setChatRunId(rid);
              setResumed(true);
              setFootSignal((n) => n + 1);
              chatCache.set(del.id, { msgs: thread, runId: rid });
              onProposalChange();
              void loadConvos();
            } else {
              settleTries.current += 1;
            }
          }
        } catch { /* transient — next poll answers */ }
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 2000);
    return () => { live = false; clearInterval(iv); };
  }, [asking, watching, chatRunId, engagementId, isMine, readThread, del.id, onProposalChange, loadConvos]);

  const sendAsk = useCallback(async () => {
    const q = ask.trim();
    if (!q || asking) return;
    ownedTurn.current = true;
    setWatching(false);
    setAsking(true);
    setAskStartedAt(Date.now());
    setChatBoth((c) => [...c, { who: 'op', text: q }]);
    setAsk('');
    try {
      // The linker's edges travel with the context: the agent must know what this deliverable
      // builds on (and what builds on it) BEFORE it proposes — a stale edge is named as such.
      // Texts elsewhere that NAME this deliverable travel with the context too: a change here
      // that breaks them should come back as cross-deliverable update proposals, not silence.
      const refbackLines = refbacks.slice(0, 10).map(
        (x) => `- ${x.ref ?? x.entity_type} (${x.field}): …${x.excerpt.replace(/\s+/g, ' ')}…`,
      );
      const linkLines = [
        ...(del.links ?? []).map((l) =>
          `It builds on ${l.to_item_ref ?? l.to_ref} (${l.to_name})${l.note ? ` — ${l.note}` : ''}${
            l.stale ? ' — STALE: that content changed and this deliverable has not been reconciled with it yet' : ''}.`),
        ...(del.linked_by ?? []).map((l) =>
          `${l.from_item_ref ?? l.from_ref} (${l.from_name}) builds on ${l.to_item_ref ?? 'this deliverable'} — content you change here may force an adaptation there.`),
      ];
      const preamble = (chatRunId === null || resumed)
        ? [
            `[Deliverable refinement — ${del.ref} · ${del.name}]`,
            `You are refining ONE deliverable of this engagement. Definition of done: ${del.definition_of_done ?? '—'}.`,
            `Due: ${del.due_date ?? 'undated'}. Description: ${del.description ?? '—'}.`,
            `Current content items: ${items.length ? items.map((it) => `“${it.title}”${it.due_date ? ` (${it.due_date})` : ''}`).join('; ') : 'none yet'}.`,
            `It satisfies: ${del.requirements.map((r) => `${r.ref} — ${r.statement ?? ''}`).join(' · ') || '—'}.`,
            ...(linkLines.length ? ['Linked dependencies:', ...linkLines.map((s) => `- ${s}`)] : []),
            ...(refbackLines.length
              ? [`Texts elsewhere NAME ${del.ref} — if a change here makes them wrong, propose updates there too:`, ...refbackLines]
              : []),
            `Propose content pieces with the propose_deliverable_item tool (deliverable_ref="${del.ref}"); use flag_open_question for anything you cannot decide. Do not propose requirements in this conversation.`,
            `The full roadmap is in register/deliverables.md. If keeping it consistent requires content in a LINKED deliverable to be created or adapted, propose that too — propose_deliverable_item with THAT deliverable_ref, or propose_update — and say in the reason why ${del.ref} forces it. Such proposals land in that deliverable's own bench and in the review queue, never directly in the record.`,
            '',
            `Operator: ${q}`,
          ].join('\n')
        : q;
      const out = await agentApi.conversation(engagementId, preamble, chatRunId, del.ref);
      setResumed(false);
      setChatBoth((c) => [...c, { who: 'agent', text: out.reply, proposals: out.proposals }], Number(out.run.id));
      void loadConvos();
    } catch (err) {
      setChatBoth((c) => [...c, { who: 'agent', text: err instanceof Error ? err.message : t('The agent did not answer.') }]);
    } finally {
      ownedTurn.current = false;
      setAsking(false);
    }
  }, [ask, del, asking, chatRunId, resumed, items, refbacks, engagementId, setChatBoth, loadConvos, t]);

  /** Accept a pending proposal from the workbench — a content item (lands in Contents) or a
   *  provisional mark (writes working_overrides directly; see StandingLine/StandingPicker's
   *  own comment on the shared shape). Either way the board's own state may need a re-read:
   *  Contents, this deliverable's own PROVISIONAL mark, or a requirement's. */
  const holdProposal = useCallback(async (proposalId: number, kind: string) => {
    try {
      await agentApi.accept(proposalId, {});
      toast('ok', kind === 'provisional'
        ? t('#{id} held — the mark is set', { id: proposalId })
        : kind.startsWith('update_')
          ? t('#{id} held — the amendment is applied', { id: proposalId })
          : kind === 'create_deliverable_item'
            ? t('#{id} held — it landed in Contents', { id: proposalId })
            : t('#{id} held', { id: proposalId }));
      await loadItems();
      onReload();
      onProposalChange();
      onStandingsChange();
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The hold was refused.')); }
  }, [loadItems, onReload, onProposalChange, onStandingsChange, toast, t]);

  // ── the dependency linker: what this deliverable builds on, and what builds on it ──
  /** The add-link row, folded behind "+ Link…" until asked for. */
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(0);
  const [linkFromItem, setLinkFromItem] = useState(0);
  const [linkToItem, setLinkToItem] = useState(0);
  const [linkNote, setLinkNote] = useState('');
  /** The chosen target's own items — fetched when the target is picked, for the optional pin. */
  const [linkToItems, setLinkToItems] = useState<DeliverableItem[]>([]);
  const [linking, setLinking] = useState(false);
  /** The target whose fetch is allowed to land — a slower response for a PREVIOUS pick is dropped. */
  const linkTargetRef = useRef(0);
  const pickLinkTarget = useCallback((targetId: number) => {
    linkTargetRef.current = targetId;
    setLinkTarget(targetId);
    setLinkToItem(0);
    setLinkToItems([]);
    if (targetId) {
      deliverablesApi.items(targetId)
        .then((its) => { if (linkTargetRef.current === targetId) setLinkToItems(its); })
        .catch(() => { if (linkTargetRef.current === targetId) setLinkToItems([]); });
    }
  }, []);
  /** Both ends re-read after every link write: the edge rides on each deliverable's payload. */
  const refreshEnds = useCallback((otherId: number | null) => {
    onOverrideChange(del.id);
    if (otherId && otherId !== del.id) onOverrideChange(otherId);
  }, [onOverrideChange, del.id]);
  const addLink = useCallback(async () => {
    if (!linkTarget || linking) return;
    setLinking(true);
    try {
      await deliverablesApi.link(del.id, {
        to_deliverable_id: linkTarget,
        from_item_id: linkFromItem || null,
        to_item_id: linkToItem || null,
        note: linkNote.trim() || null,
      });
      setLinkOpen(false); setLinkTarget(0); setLinkFromItem(0); setLinkToItem(0); setLinkNote(''); setLinkToItems([]);
      refreshEnds(linkTarget);
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The link was refused.')); }
    finally { setLinking(false); }
  }, [del.id, linkTarget, linkFromItem, linkToItem, linkNote, linking, refreshEnds, toast, t]);
  const removeLink = useCallback(async (link: DeliverableLink) => {
    try {
      await deliverablesApi.unlink(del.id, link.id);
      refreshEnds(link.from_deliverable_id === del.id ? link.to_deliverable_id : link.from_deliverable_id);
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [del.id, refreshEnds, toast, t]);
  const confirmLink = useCallback(async (link: DeliverableLink) => {
    try {
      await deliverablesApi.confirmLink(del.id, link.id);
      refreshEnds(link.to_deliverable_id === del.id ? link.from_deliverable_id : link.to_deliverable_id);
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [del.id, refreshEnds, toast, t]);
  /**
   * A stale link's one-click follow-up: open the chat pre-seeded with the adaptation ask. The
   * operator still reads and sends it themselves — the agent is INVITED to reconcile, never
   * auto-run off a ribbon click (P1 applies to prompts too: nothing fires that nobody typed
   * or at least saw).
   */
  const adaptWithAgent = useCallback((link: DeliverableLink) => {
    setChatFold(true);
    setAsk(t(
      '{ref} — the content this deliverable builds on in {other} has changed. Compare against {other} and propose adaptations to my content items here.',
      { ref: del.ref, other: link.to_item_ref ?? link.to_ref },
    ));
    setAskFocus((n) => n + 1);
  }, [del.ref, setChatFold, t]);

  /** Rub an assumption out. The record never moved, so there is nothing on it to undo. */
  const revertOverride = useCallback(async (id: number) => {
    try {
      await registerApi.revertOverride(id, engagementId);
      onOverrideChange(del.id);
      await loadItems();
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [engagementId, onOverrideChange, del.id, loadItems, toast, t]);

  /** Unmap a requirement from this deliverable — the link goes, the requirement stays. */
  const unmapReq = useCallback(async (requirementId: number, reqRef: string) => {
    try {
      await deliverablesApi.unmapRequirement(del.id, requirementId);
      onOverrideChange(del.id);
      toast('ok', t('{ref} unmapped — the requirement itself stays', { ref: reqRef }));
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [del.id, onOverrideChange, toast, t]);

  /** Clear a PROVISIONAL mark — on this deliverable, or on one of the requirements it satisfies. */
  const revertStanding = useCallback(async (id: number, onOf: 'deliverable' | 'requirement') => {
    try {
      await registerApi.revertOverride(id, engagementId);
      if (onOf === 'deliverable') onOverrideChange(del.id); else onStandingsChange();
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The change was refused.')); }
  }, [engagementId, onOverrideChange, onStandingsChange, del.id, toast, t]);

  /** The obvious tethers for THIS deliverable — computed server-side from its requirements' refs. */
  const preferredQs = del.open_questions ?? [];
  /** THIS deliverable's own PROVISIONAL mark, if any — rides in the payload already fetched. */
  const delStanding = standingOf(del.overrides);

  const docRef = (id: number): string => docs.find((x) => x.id === id)?.ref ?? `#${id}`;
  const toggleItem = (id: number) => setOpenItems((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /** What an untreated proposal says, in one line — a content item's title, or a provisional
   *  proposal's own entity ref and reason (propose_provisional's payload). */
  const proposalLabel = (p: Proposal): string => {
    const payload = p.payload as Record<string, unknown>;
    if (p.descriptor) return p.descriptor;
    const kindStr = p.kind as unknown as string;
    // An amendment names itself the way the rail does: the ref plus what would change.
    if (kindStr === 'update_deliverable' || kindStr === 'update_deliverable_item') {
      return proposalSubject(kindStr, payload) || `#${p.id}`;
    }
    if ((p.kind as unknown as string) === 'provisional') {
      const ref = String(payload.entity_ref ?? '');
      const reason = String(payload.reason ?? '');
      return ref ? `◈ ${ref}${reason ? ` — ${reason}` : ''}` : t('a provisional mark');
    }
    return String(payload.title ?? `#${p.id}`);
  };

  /** The thread in the shared flyout's shape — proposal cards ride each agent turn as extras. */
  const flyoutMsgs = useMemo<FlyoutMsg[]>(() => chat.map((m) => ({
    who: m.who,
    text: m.text,
    extras: m.who === 'agent' && m.proposals?.length ? (
      <>
        {m.proposals.map((p) => {
          const kindStr = p.kind as unknown as string;
          const payload = p.payload as Record<string, unknown>;
          // A proposal that lands in ANOTHER deliverable says so on its card —
          // it appears in that bench and in the review queue, not in this one.
          const landing = proposalDeliverableRef(kindStr, payload);
          return (
            <div className="rw-prop" key={p.id}>
              <span className="mark">◇</span>
              {landing && landing !== del.ref ? (
                <span
                  className="src-ref rw-crossref"
                  title={t('This proposal lands in {ref} — it appears in that bench and in the review queue', { ref: landing })}
                >→ {landing}</span>
              ) : null}
              <span className="t">{proposalSubject(kindStr, payload) || String(payload.title ?? payload.statement ?? `#${p.id}`)}</span>
              {HOLDABLE_IN_THREAD.has(kindStr) && p.status === 'pending' ? (
                <button type="button" className="lnkb" onClick={() => void holdProposal(p.id, p.kind)}>{t('Hold')}</button>
              ) : (
                // Only what still WAITS is "in the queue" — a reopened thread replays cards
                // with their real statuses, and a held one must not claim to be queued.
                <span className="q">{p.status === 'pending' ? `${t(p.status)} · ${t('in the queue')}` : t(p.status)}</span>
              )}
            </div>
          );
        })}
      </>
    ) : undefined,
  })), [chat, del.ref, holdProposal, t]);

  return (
    <section className="rw-wb" ref={rootRef} aria-label={t('Workbench {ref}', { ref: del.ref })}>
      <div className="rw-wb-head">
        <span className="src-ref" style={{ color: 'var(--ink-3)' }}>{del.ref}</span>
        {editing === 'name' ? (
          <input
            className="rw-name"
            defaultValue={del.name}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            onBlur={(e) => { const v = e.target.value.trim(); setEditing(null); if (v && v !== del.name) void patchDeliverable(del.id, { name: v }, t('{ref} renamed', { ref: del.ref })); }}
          />
        ) : (
          <h2 className="rw-name-h">
            <Tx type="deliverable" id={del.id} field="name" original={del.name} />
            <button type="button" className="rw-editbtn" title={t('Edit the name')} onClick={() => setEditing('name')}>✎</button>
          </h2>
        )}
        <span className="due">{t('due {date}', { date: fmtDate(del.due_date) })}</span>
        <span className="state">{t(del.status)}</span>
        {untreatedProposals.length ? (
          <span
            className="rw-untreated"
            title={tp(
              untreatedProposals.length,
              '{n} proposal from the agent is still waiting on a Hold or a decision',
              '{n} proposals from the agent are still waiting on a Hold or a decision',
            )}
          >
            <span className="att att--proposed" aria-hidden="true" />
            <span className="tag-unheld">{tp(untreatedProposals.length, '{n} untreated', '{n} untreated')}</span>
          </span>
        ) : null}
        {delStanding ? (
          <StandingLine o={delStanding} onRevert={() => void revertStanding(delStanding.id, 'deliverable')} onGoToQuestion={onGoToQuestion} />
        ) : (
          <button
            type="button"
            className="rw-pencilbtn"
            title={t('Mark this deliverable provisional, tethered to an open question — a standing override, not the record itself')}
            onClick={() => setPencil(pencil === 'del-standing' ? null : 'del-standing')}
          >◈ {t('mark provisional')}</button>
        )}
        <span className="rw-wb-nav">
          <button type="button" disabled={!hasPrev} onClick={() => onStep(-1)} title={t('Previous deliverable in this bench')}>‹</button>
          <button type="button" disabled={!hasNext} onClick={() => onStep(1)} title={t('Next deliverable in this bench')}>›</button>
          <button type="button" className="x" onClick={onClose} title={t('Close this bench')}>×</button>
        </span>
      </div>
      {pencil === 'del-standing' ? (
        <div className="rw-standing-picker">
          <StandingPicker
            engagementId={engagementId}
            entityType="deliverable"
            entityId={del.id}
            all={openQuestions}
            onDone={() => { setPencil(null); onOverrideChange(del.id); }}
            onCancel={() => setPencil(null)}
          />
        </div>
      ) : null}
      <div className="rw-wb-body">
        {/* ── THE CHAT, on the left, foldable ────────────────────────────────────────
            The dock bounds the bench's height, so this is a real chat client and not a
            column that grows for ever: three zones, and only the middle one scrolls. A
            short thread sits at the FOOT and grows upward; a new turn only scrolls down
            if the operator was already watching the foot. Reading history always wins. */}
        <AgentFlyout
          open={chatOpen}
          onFold={setChatFold}
          label={t('Refine with the agent')}
          byline={t('agent · proposes into the queue, writes nothing')}
          sessionLabel={chatRunId ? t('run #{id}', { id: chatRunId }) : t('New conversation')}
          headExtra={
            <button
              type="button"
              className="rw-convsw"
              aria-expanded={convosOpen}
              onClick={() => { setConvosOpen((v) => !v); void loadConvos(); }}
            >
              <span className="u-label">{t('Conversations')}</span>
              <span className="chev" aria-hidden="true">▾</span>
            </button>
          }
          emptyState={
            <>
              <p className="rw-agent-note">{t('A conversation scoped to {ref} — it is kept for good and comes back whenever you reopen the bench.', { ref: del.ref })}</p>
              {/* P1, said once where it is read — every agent turn repeats it in its own byline. */}
              <p className="rw-agent-note">
                {t('The agent proposes, you hold — held items land in Contents with their provenance; refusals keep their reason and teach the next run.')}
              </p>
            </>
          }
          msgs={flyoutMsgs}
          busy={asking}
          activity={turnAct}
          busyStartedAt={askStartedAt}
          ask={ask}
          onAskChange={setAsk}
          onSend={() => void sendAsk()}
          placeholder={t('Ask — e.g. "Break {name} down further"', { name: del.name.split(':')[0] })}
          focusSignal={askFocus}
          footSignal={footSignal}
          overlay={convosOpen ? (
                <div className="rw-convpop" role="dialog" aria-label={t('Conversations')}>
                  <div className="rw-convpop-head">
                    <span className="rw-convrail-tabs">
                      <button type="button" className={railTab === 'mine' ? 'on' : ''} onClick={() => setRailTab('mine')}>{t('this deliverable')}</button>
                      <button type="button" className={railTab === 'all' ? 'on' : ''} onClick={() => { setRailTab('all'); void loadConvos(); }}>{t('all')}</button>
                    </span>
                    <button type="button" className="x" title={t('Close the conversation list')} onClick={() => setConvosOpen(false)}>×</button>
                  </div>
                  <button type="button" className="rw-convnew" onClick={() => { newConversation(); setConvosOpen(false); }}>{t('+ New conversation')}</button>
                  <div className="rw-convcards">
                    {convos === null ? <div className="rw-agent-note">{t('reading the conversations…')}</div>
                      : (railTab === 'mine' ? convos.filter(isMine) : convos).map((r) => (
                        <button
                          type="button"
                          className={`rw-convcard${chatRunId === r.id ? ' on' : ''}`}
                          key={r.id}
                          onClick={() => void openConversation(r)}
                        >
                          <span className="top">
                            <span className="src-ref">#{r.id}</span>
                            {railTab === 'all' && isMine(r) ? <span className="src-ref mine">{del.ref}</span> : null}
                            <span className="d voice-figure">{fmtDate(String(r.created_at ?? '').slice(0, 10))}</span>
                          </span>
                          <span className="t">{operatorWords((r.prompt ?? '').replace(/\s+/g, ' ')).slice(0, 90) || t('conversation')}</span>
                        </button>
                      ))}
                    {convos !== null && (railTab === 'mine' ? convos.filter(isMine) : convos).length === 0 ? (
                      <div className="rw-agent-note">{t('No conversations here yet.')}</div>
                    ) : null}
                  </div>
                </div>
          ) : null}
        />

        {/* ── THE DELIVERABLE, on the right: what it is, when it is due, what it contains,
            and — below Contents — the requirements it satisfies. Its own scroll. ────── */}
        <div className="rw-wb-doc">
          {untreatedProposals.length ? (
            <div className="rw-sec rw-untreated-sec">
              <div className="rw-sec-head">
                <span className="u-label">{t('Untreated')}</span>
                <span className="hint">{t('◇ said by the agent, not yet judged')}</span>
              </div>
              {untreatedProposals.map((p) => (
                <div className="rw-prop" key={p.id}>
                  <span className="mark">◇</span>
                  {p.run_context_ref && p.run_context_ref !== del.ref ? (
                    // 'ROADMAP' is the planner's marker, not a bench — name it as what it is.
                    <span
                      className="src-ref rw-crossref"
                      title={p.run_context_ref === 'ROADMAP'
                        ? t('Proposed by the roadmap planner — it judged the plan as a whole')
                        : t('Proposed from the {ref} workbench — its refinement needs something here', { ref: p.run_context_ref })}
                    >{p.run_context_ref === 'ROADMAP' ? t('from the planner') : t('from {ref}', { ref: p.run_context_ref })}</span>
                  ) : null}
                  <span className="t">{proposalLabel(p)}</span>
                  <button type="button" className="lnkb" onClick={() => void holdProposal(p.id, p.kind)}>{t('Hold')}</button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="rw-block">
            <span className="u-label">{t('Description')}
              <button type="button" className="rw-editbtn" title={t('Edit the description')} onClick={() => setEditing('desc')}>✎</button>
            </span>
            {editing === 'desc' ? (
              <>
                <AutoGrow
                  value={del.description ?? ''}
                  refOptions={benchRefOptions}
                  placeholder={t('What this deliverable is.')}
                  onDone={(v) => { setEditing(null); const txt = v.trim(); if ((txt || null) !== (del.description ?? null)) void patchDeliverable(del.id, { description: txt }, t('{ref} description saved', { ref: del.ref })); }}
                />
                <div className="rw-hintline">{t(HINT)}</div>
              </>
            ) : (del.description ?? '').trim() ? (
              <Tx type="deliverable" id={del.id} field="description" original={del.description}
                  render={(txt) => <Rich className="rw-rich" text={txt} />} />
            ) : (
              <div className="rw-none">{t('— no description yet')}</div>
            )}
          </div>
          <div className="rw-dod">
            <span className="u-label">{t('Definition of done')}
              <button type="button" className="rw-editbtn" title={t('Edit the definition of done')} onClick={() => setEditing('dod')}>✎</button>
            </span>
            {editing === 'dod' ? (
              <>
                <AutoGrow
                  value={del.definition_of_done ?? ''}
                  refOptions={benchRefOptions}
                  placeholder={t('The observable condition under which this is finished.')}
                  onDone={(v) => { setEditing(null); const txt = v.trim(); if ((txt || null) !== (del.definition_of_done ?? null)) void patchDeliverable(del.id, { definition_of_done: txt }, t('{ref} definition of done saved', { ref: del.ref })); }}
                />
                <div className="rw-hintline">{t(HINT)}</div>
              </>
            ) : (del.definition_of_done ?? '').trim() ? (
              <Tx type="deliverable" id={del.id} field="definition_of_done" original={del.definition_of_done}
                  render={(txt) => <Rich className="rw-rich" text={txt} />} />
            ) : (
              <div className="rw-none">{t('— not defined yet')}</div>
            )}
          </div>

          {/* ── the dates: the record, and the assumptions planned beside it ────────────
              The record's dates are moved on the roadmap (a drag) — here they are stated, and
              beside each one sits the pencil. An assumption never overwrites the value to its
              left; it stands next to it until a client call settles its question. */}
          <div className="rw-sec rw-dates">
            <div className="rw-sec-head">
              <span className="u-label">{t('Dates')}</span>
              <span className="hint">{t('the record · an assumption is planned beside it, never over it')}</span>
            </div>
            {(['start_date', 'due_date'] as const).map((field) => {
              const o = overrideOf(del.overrides, field);
              const rec = field === 'due_date' ? del.due_date : del.start_date;
              const key = `del-${field}`;
              return (
                <Fragment key={field}>
                  <div className="row">
                    <span className="u-label">{field === 'due_date' ? t('Due') : t('Start')}</span>
                    <span className={`voice-figure${rec ? '' : ' none'}`}>{rec ? fmtDate(rec) : t('undated')}</span>
                    {o ? <GhostLine o={o} onRevert={() => void revertOverride(o.id)} /> : null}
                    <button
                      type="button"
                      className="rw-pencilbtn"
                      title={t('Plan this date as an assumption, tethered to an open question — the record does not move')}
                      onClick={() => setPencil(pencil === key ? null : key)}
                    >✎ {t('as an assumption')}</button>
                  </div>
                  {pencil === key ? (
                    <Pencil
                      engagementId={engagementId}
                      entityType="deliverable"
                      entityId={del.id}
                      field={field}
                      recordValue={rec}
                      existing={o}
                      preferred={preferredQs}
                      all={openQuestions}
                      onDone={() => { setPencil(null); onOverrideChange(del.id); }}
                      onCancel={() => setPencil(null)}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </div>

          <div className="rw-sec">
            <div className="rw-sec-head">
              <span className="u-label">{t('Contents')}</span>
              <span className="hint">{t('sorted by date · ◇ drafted · ▪ held')}</span>
            </div>
            {items.length ? items.map((it, idx) => (
              <div className={`rw-ci${openItems.has(it.id) ? ' open' : ''}${it.status === 'held' ? ' held' : ''}`} key={it.id}>
                <div
                  className="rw-ci-h"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleItem(it.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') toggleItem(it.id); }}
                >
                  <span className="mark">{it.status === 'held' ? '▪' : '◇'}</span>
                  {it.ref ? <span className="src-ref rw-ci-ref">{it.ref}</span> : null}
                  {editing === `title-${it.id}` ? (
                    <input
                      className="rw-ci-title"
                      defaultValue={it.title}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      // The row itself is the accordion's button — its Enter must not reach it.
                      onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      onBlur={(e) => { const v = e.target.value.trim(); setEditing(null); if (v && v !== it.title) void saveItem(it.id, { title: v }); }}
                    />
                  ) : (
                    <span className="txt">
                      <Tx type="deliverable_item" id={it.id} field="title" original={it.title} />
                      <button
                        type="button"
                        className="rw-editbtn"
                        title={t('Edit the title')}
                        onClick={(e) => { e.stopPropagation(); setEditing(`title-${it.id}`); }}
                      >✎</button>
                    </span>
                  )}
                  {it.requirement_ids.length ? (
                    <span className="lnks src-ref">{it.requirement_ids.map((rid) => rowOf(rid)?.ref.slice(4) ?? rid).join(' ')}</span>
                  ) : null}
                  <span className={`date voice-figure${it.due_date ? '' : ' none'}`}>{it.due_date ? fmtDate(it.due_date) : t('undated')}</span>
                  <span className="ud" onClick={(e) => e.stopPropagation()}>
                    <button type="button" disabled={idx === 0} onClick={() => void moveItem(idx, -1)} title={t('Move up')}>↑</button>
                    <button type="button" disabled={idx === items.length - 1} onClick={() => void moveItem(idx, 1)} title={t('Move down')}>↓</button>
                  </span>
                  <span className="chev" aria-hidden="true">▶</span>
                </div>
                <div className="rw-ci-b"><div><div className="rw-ci-in">
                  {editing === `spec-${it.id}` ? (
                    <>
                      <AutoGrow
                        value={it.spec ?? ''}
                        refOptions={benchRefOptions}
                        placeholder={t('Specify this piece — what it contains, how it is judged…')}
                        onDone={(v) => { setEditing(null); if (v !== (it.spec ?? '')) void saveItem(it.id, { spec: v || null }); }}
                      />
                      <div className="rw-hintline">{t(HINT)}</div>
                    </>
                  ) : (
                    <div className="rw-specrow">
                      {(it.spec ?? '').trim()
                        ? <Tx type="deliverable_item" id={it.id} field="spec" original={it.spec}
                              render={(txt) => <Rich className="rw-rich" text={txt} />} />
                        : <span className="rw-none">{t('— no specification yet')}</span>}
                      <button type="button" className="rw-editbtn" title={t('Edit the specification')} onClick={() => setEditing(`spec-${it.id}`)}>✎</button>
                    </div>
                  )}
                  <div className="row">
                    <span className="u-label">{t('Due')}</span>
                    <input
                      type="date"
                      defaultValue={it.due_date ?? ''}
                      onBlur={(e) => { const v = e.target.value || null; if (v !== it.due_date) void saveItem(it.id, { due_date: v }); }}
                    />
                    <span className="q">{t('changing the date re-sorts the list')}</span>
                    {overrideOf(it.overrides, 'due_date') ? (
                      <GhostLine o={overrideOf(it.overrides, 'due_date')!} onRevert={() => void revertOverride(overrideOf(it.overrides, 'due_date')!.id)} />
                    ) : null}
                    <button
                      type="button"
                      className="rw-pencilbtn"
                      title={t('Plan this date as an assumption, tethered to an open question — the record does not move')}
                      onClick={() => setPencil(pencil === `item-${it.id}` ? null : `item-${it.id}`)}
                    >✎ {t('as an assumption')}</button>
                  </div>
                  {pencil === `item-${it.id}` ? (
                    <Pencil
                      engagementId={engagementId}
                      entityType="deliverable_item"
                      entityId={it.id}
                      field="due_date"
                      recordValue={it.due_date}
                      existing={overrideOf(it.overrides, 'due_date')}
                      preferred={preferredQs}
                      all={openQuestions}
                      onDone={() => { setPencil(null); void loadItems(); }}
                      onCancel={() => setPencil(null)}
                    />
                  ) : null}
                  <div className="row">
                    <span className="u-label">{t('Satisfies')}</span>
                    {del.requirements.map((q) => {
                      const on = it.requirement_ids.includes(q.id);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          className={`rw-reqchip${on ? ' on' : ''}`}
                          title={q.statement ?? q.ref}
                          onClick={() => void saveItem(it.id, {
                            requirement_ids: on ? it.requirement_ids.filter((x) => x !== q.id) : [...it.requirement_ids, q.id],
                          })}
                        >
                          {q.ref}
                        </button>
                      );
                    })}
                    <span className="q">{t('which of this deliverable’s requirements the piece serves')}</span>
                  </div>
                  <div className="row">
                    <span className="u-label">{t('Documents')}</span>
                    {it.document_ids.map((dId) => (
                      <span className="doc" key={dId}>
                        {docRef(dId)}
                        <button
                          type="button"
                          title={t('Detach')}
                          onClick={() => void saveItem(it.id, { document_ids: it.document_ids.filter((x) => x !== dId) })}
                        >×</button>
                      </span>
                    ))}
                    <select
                      value=""
                      onChange={(e) => {
                        const dId = Number(e.target.value);
                        if (dId && !it.document_ids.includes(dId)) void saveItem(it.id, { document_ids: [...it.document_ids, dId] });
                      }}
                    >
                      <option value="">{t('+ Attach…')}</option>
                      {docs.filter((x) => !it.document_ids.includes(x.id)).map((x) => (
                        <option key={x.id} value={x.id}>{x.ref}</option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <span className="u-label">{t('Provenance')}</span>
                    {it.ref ? <span className="src-ref">{it.ref}</span> : null}
                    <span className="doc">{t(it.provenance ?? 'operator')}</span>
                    <span className="sp" />
                    {it.status !== 'held' ? (
                      <button type="button" className="lnkb" onClick={() => void saveItem(it.id, { status: 'held' })}>{t('Hold ▪')}</button>
                    ) : (
                      <button type="button" className="lnkb" onClick={() => void saveItem(it.id, { status: 'draft' })}>{t('Back to draft ◇')}</button>
                    )}
                    <button
                      type="button"
                      className="lnkb danger"
                      onClick={() => { void deliverablesApi.removeItem(it.id).then(() => void loadItems()); }}
                    >{t('Drop')}</button>
                  </div>
                </div></div></div>
              </div>
            )) : <div className="rw-empty">{t('No content items yet — add one below, or ask the agent.')}</div>}
            <div className="rw-add">
              <input
                value={newItem}
                placeholder={t('Add a content item by hand…')}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void addItem(); }}
              />
              <button type="button" className="btn btn-sm" onClick={() => void addItem()}>{t('Add')}</button>
            </div>
          </div>

          <div className="rw-sec">
            <div className="rw-sec-head">
              <span className="u-label">{t('Satisfies')}</span>
              <span className="hint">{tp(del.requirements.length, '{n} requirement · a source ref opens the span', '{n} requirements · a source ref opens the span')}</span>
            </div>
            {del.requirements.map((q) => {
              const full = rowOf(q.id);
              const reqStanding = requirementStandings.get(q.id) ?? null;
              const reqUntreated = untreatedByReq.get(q.id) ?? EMPTY_PROPOSALS;
              return (
                <div className="rw-req" key={q.id}>
                  <div className="rh">
                    <span className="src-ref">{q.ref}</span>
                    <span className="w">{t(q.priority === 'wont' ? "won't" : q.priority)} · {t(q.status)}</span>
                    {/* An OPEN question names this ref — whatever it says is still being argued. */}
                    {q.in_clarification ? (
                      <span className="rw-clarify" title={t('An open question names {ref} — this ground is not settled yet', { ref: q.ref })}>
                        <span className="att att--proposed" aria-hidden="true" />
                        <span className="tag-unheld">{t('under clarification')}</span>
                      </span>
                    ) : null}
                    {reqUntreated.length ? (
                      <span
                        className="rw-untreated"
                        title={tp(
                          reqUntreated.length,
                          'The agent has proposed a provisional mark here — not yet judged',
                          'The agent has proposed provisional marks here — not yet judged',
                        )}
                      >
                        <span className="att att--proposed" aria-hidden="true" />
                        <span className="tag-unheld">{t('untreated')}</span>
                      </span>
                    ) : null}
                    {reqStanding ? (
                      <StandingLine o={reqStanding} onRevert={() => void revertStanding(reqStanding.id, 'requirement')} onGoToQuestion={onGoToQuestion} />
                    ) : (
                      <button
                        type="button"
                        className="rw-pencilbtn"
                        title={t('Mark {ref} provisional, tethered to an open question — a standing override, not the record itself', { ref: q.ref })}
                        onClick={() => setPencil(pencil === `req-standing-${q.id}` ? null : `req-standing-${q.id}`)}
                      >◈ {t('mark provisional')}</button>
                    )}
                    <span className="srcs">
                      {(full?.source_document_refs ?? []).map((dr) => (
                        <button
                          key={dr}
                          type="button"
                          className="reg-srcbtn src-ref"
                          title={t('Show the span {ref} cites in {doc}', { ref: q.ref, doc: dr })}
                          onClick={() => onOpenSpan(q.id, dr)}
                        >{dr}</button>
                      ))}
                    </span>
                    <button
                      type="button"
                      className="x"
                      title={t('Unmap {ref} from this deliverable — the requirement itself stays', { ref: q.ref })}
                      onClick={() => void unmapReq(q.id, q.ref)}
                    >×</button>
                  </div>
                  {pencil === `req-standing-${q.id}` ? (
                    <div className="rw-standing-picker">
                      <StandingPicker
                        engagementId={engagementId}
                        entityType="requirement"
                        entityId={q.id}
                        all={openQuestions}
                        onDone={() => { setPencil(null); onStandingsChange(); }}
                        onCancel={() => setPencil(null)}
                      />
                    </div>
                  ) : null}
                  {q.statement ? <p>{q.statement}</p> : null}
                </div>
              );
            })}
          </div>

          {/* ── THE DEPENDENCY LINKER: whose content this deliverable builds on, and who
              builds on it. Directed — when the other side's content changes, the edge goes
              stale and THIS side carries the "linked content changed" flag with its two
              answers: adapt (the chat, pre-seeded) or declare it reconciled. A ref chip
              opens the other bench BESIDE this one — the dock already does side by side. */}
          <div className="rw-sec rw-links">
            <div className="rw-sec-head">
              <span className="u-label">{t('Linked')}</span>
              <span className="hint">{t('builds on · when the linked content changes, this side is flagged')}</span>
              <button type="button" className="lnkb" onClick={() => setLinkOpen((v) => !v)}>
                {linkOpen ? t('Cancel') : t('+ Link…')}
              </button>
            </div>
            {linkOpen ? (
              <div className="rw-linkadd">
                <span className="u-label">{t('builds on')}</span>
                <select
                  value={linkTarget}
                  aria-label={t('The deliverable this one builds on')}
                  onChange={(e) => pickLinkTarget(Number(e.target.value))}
                >
                  <option value={0}>{t('pick a deliverable…')}</option>
                  {allDels.filter((d) => d.id !== del.id).map((d) => (
                    <option key={d.id} value={d.id}>{d.ref} — {d.name}</option>
                  ))}
                </select>
                {items.length ? (
                  <select
                    value={linkFromItem}
                    aria-label={t('Pin this side to one of its content items — optional')}
                    onChange={(e) => setLinkFromItem(Number(e.target.value))}
                  >
                    <option value={0}>{t('this whole deliverable')}</option>
                    {items.map((it) => <option key={it.id} value={it.id}>{it.ref ?? `#${it.id}`} — {it.title}</option>)}
                  </select>
                ) : null}
                {linkTarget && linkToItems.length ? (
                  <select
                    value={linkToItem}
                    aria-label={t('Pin the other side to one of its content items — optional')}
                    onChange={(e) => setLinkToItem(Number(e.target.value))}
                  >
                    <option value={0}>{t('their whole deliverable')}</option>
                    {linkToItems.map((it) => <option key={it.id} value={it.id}>{it.ref ?? `#${it.id}`} — {it.title}</option>)}
                  </select>
                ) : null}
                <input
                  className="nt"
                  value={linkNote}
                  placeholder={t('why, in one line — optional')}
                  onChange={(e) => setLinkNote(e.target.value)}
                />
                <button type="button" className="btn btn-sm btn-accent" disabled={!linkTarget || linking} onClick={() => void addLink()}>
                  {t('Link')}
                </button>
              </div>
            ) : null}
            {(del.links ?? []).map((l) => (
              <div className={`rw-linkrow${l.stale ? ' stale' : ''}`} key={`out-${l.id}`}>
                <span className="q">{t('builds on')}</span>
                <button
                  type="button"
                  className="src-ref rw-qlink"
                  title={t('Open the {ref} bench beside this one', { ref: l.to_ref })}
                  onClick={() => onOpenBench(l.to_deliverable_id)}
                >{l.to_item_ref ?? l.to_ref}</button>
                <span className="t">
                  {l.to_item_title ?? l.to_name}
                  {l.from_item_ref ? ` · ${t('via {ref}', { ref: l.from_item_ref })}` : ''}
                </span>
                {l.provenance === 'agent' ? (
                  <span className="doc" title={t('Recorded when a cross-deliverable proposal was held')}>{t('agent')}</span>
                ) : null}
                {l.note ? <span className="nt">{l.note}</span> : null}
                {l.stale ? (
                  <>
                    <span className="rw-stale" title={t('The linked content changed after this deliverable last reconciled with it')}>
                      <span className="att att--proposed" aria-hidden="true" />
                      <span className="tag-unheld">{t('linked content changed')}</span>
                    </span>
                    <button type="button" className="lnkb" onClick={() => adaptWithAgent(l)}>{t('Adapt with the agent')}</button>
                    <button
                      type="button"
                      className="lnkb"
                      title={t('Mark it reconciled — nothing needed adapting')}
                      onClick={() => void confirmLink(l)}
                    >{t('Reconciled ✓')}</button>
                  </>
                ) : null}
                <button type="button" className="x" title={t('Remove this link')} onClick={() => void removeLink(l)}>×</button>
              </div>
            ))}
            {(del.linked_by ?? []).map((l) => (
              <div className="rw-linkrow in" key={`in-${l.id}`}>
                <button
                  type="button"
                  className="src-ref rw-qlink"
                  title={t('Open the {ref} bench beside this one', { ref: l.from_ref })}
                  onClick={() => onOpenBench(l.from_deliverable_id)}
                >{l.from_item_ref ?? l.from_ref}</button>
                <span className="q">{l.to_item_ref ? t('builds on {ref}', { ref: l.to_item_ref }) : t('builds on this')}</span>
                {l.note ? <span className="nt">{l.note}</span> : null}
                <button type="button" className="x" title={t('Remove this link')} onClick={() => void removeLink(l)}>×</button>
              </div>
            ))}
            {!(del.links ?? []).length && !(del.linked_by ?? []).length && !linkOpen ? (
              <div className="rw-none">{t('— nothing linked yet · a link says whose content this deliverable builds on')}</div>
            ) : null}
          </div>

          {/* ── WHO NAMES THIS REF: every text in the register that writes this deliverable's
              ref — derived from the prose itself, never stored, so it cannot lie. Editing
              here with these in view is the point; a chip on another deliverable's text opens
              that bench beside this one. */}
          {refbacks.length ? (
            <div className="rw-sec">
              <div className="rw-sec-head">
                <span className="u-label">{t('Referenced in')}</span>
                <span className="hint">{t('texts that name {ref} — change it and they may need to move along', { ref: del.ref })}</span>
              </div>
              <div className="rw-refbacks">
                {refbacks.map((x, i) => {
                  // A row with no ref shows its NAME, never a raw snake_case entity_type.
                  const name = x.ref ?? (x.label?.trim() ? x.label.slice(0, 28) : x.entity_type.replace(/_/g, ' '));
                  return x.deliverable_id && x.deliverable_id !== del.id ? (
                    <button
                      key={`${x.entity_type}-${x.entity_id}-${x.field}-${i}`}
                      type="button"
                      className="src-ref rw-qlink"
                      title={`${x.label ?? ''}\n…${x.excerpt}…`}
                      onClick={() => onOpenBench(x.deliverable_id!)}
                    >{name}</button>
                  ) : (
                    <span
                      key={`${x.entity_type}-${x.entity_id}-${x.field}-${i}`}
                      className="src-ref"
                      title={`${x.label ?? ''}\n…${x.excerpt}…`}
                    >{name}</span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// THE BOARD — the timeline, the tray, and the dock of open benches.
// ═══════════════════════════════════════════════════════════════════════════════════════
export default function RoadmapView({
  engagementId, deliverables, rows, onOpenSpan, onReload, onOpenQuestion, refreshSignal,
}: {
  engagementId: number;
  deliverables: Deliverable[];
  /** The full register rows — the bench links each requirement to its source spans with them. */
  rows: RegisterRow[];
  onOpenSpan: (requirementId: number, docRef: string) => void;
  onReload: () => void;
  /** Bumped by the page when the planner (its sibling since it left this file) held or filed
   *  proposals — the board re-reads its ◇ marks and standings without a remount. */
  refreshSignal?: number;
  /**
   * Lead to an open question elsewhere in the app, given its id — what a ◈ PROVISIONAL chip's
   * ref leads to. Optional: with no caller-supplied cursor, the board falls back to opening the
   * register's Questions tab in this engagement (a small agenda; the ref is already named on
   * the chip). Pass this once the caller can cursor a specific question (RegisterPage already
   * has that exact mechanism for the discussion column — `setQTab('open'); setQCursor(id);
   * setTab('questions')` — this is the same jump, offered as a prop instead of assumed).
   */
  onOpenQuestion?: (questionId: number) => void;
}) {
  const toast = useToast();
  const { t, tp, fmtDate } = useLocale();
  const nav = useEngagement();
  const openRoute = useOpenRoute(nav);
  const goToQuestion = useCallback(
    (questionId: number) => {
      if (onOpenQuestion) { onOpenQuestion(questionId); return; }
      void openRoute(ROUTES.register({ tab: 'questions', engagement: engagementId }));
    },
    [onOpenQuestion, openRoute, engagementId],
  );
  const [dels, setDels] = useState<Deliverable[]>(deliverables);
  useEffect(() => { setDels(deliverables); }, [deliverables]);
  /** Every standing (PROVISIONAL) override of the engagement — deliverables AND requirements.
   *  A deliverable's own mark also rides on its `overrides` (deliverables.ts embeds it); this
   *  engagement-wide read is what gets a REQUIREMENT's mark to the workbench, since a
   *  requirement carries no `overrides` of its own on the wire (registerQueries.ts TODO). */
  const [standings, setStandings] = useState<StandingOverride[]>([]);
  const loadStandings = useCallback(async () => {
    try {
      const raw = await registerApi.overrides(engagementId);
      setStandings((raw as unknown as StandingOverride[]).filter((o) => o.field === 'standing'));
    } catch { setStandings([]); }
  }, [engagementId]);
  useEffect(() => { void loadStandings(); }, [loadStandings]);
  const requirementStandings = useMemo(() => {
    const m = new Map<number, StandingOverride>();
    for (const o of standings) if (o.entity_type === 'requirement') m.set(o.entity_id, o);
    return m;
  }, [standings]);

  /**
   * Pending proposals that stand against an entity already on this board — ◇ UNTREATED.
   *   · `create_deliverable_item` — payload.deliverable_id names an existing deliverable.
   *   · `provisional` — the agent's own propose_provisional (server/routes/agentTools.ts);
   *     payload.entity_type/entity_id name a requirement OR a deliverable. Accepting one writes
   *     working_overrides directly (server/routes/agent.ts's accept handler) — the exact same
   *     row shape StandingPicker writes — so a held one becomes a ◈ PROVISIONAL mark here with
   *     no further wiring. `Proposal.kind`'s client type predates this kind (another lane's
   *     file, src/client/api/agent.ts) — read past it the same way StandingOverride reads past
   *     WorkingOverride's date-only union.
   *   `create_deliverable` is deliberately excluded: it creates a deliverable that does not
   *   exist until accepted, so there is no card on this board yet for one to stand against.
   */
  const [pendingBoardProposals, setPendingBoardProposals] = useState<Proposal[]>([]);
  const loadPendingProposals = useCallback(async () => {
    try {
      // Amendments ride too since the linker landed: a pending update_deliverable naming
      // DEL-04 belongs on DEL-04's card and bench, wherever it was proposed from — and a
      // cross-deliverable conversation proposal (run_context_ref ≠ landing) reaches this
      // list at all only because the queue predicate carves it in (utils/proposalQueue.ts).
      const res = await agentApi.listProposals(engagementId, {
        status: 'pending',
        kind: 'create_deliverable_item,provisional,update_deliverable,update_deliverable_item',
      });
      setPendingBoardProposals(res.proposals);
    } catch { setPendingBoardProposals([]); }
  }, [engagementId]);
  useEffect(() => { void loadPendingProposals(); }, [loadPendingProposals]);
  // The planner mounts at the tab level now (RegisterPage) — when it holds or files, the page
  // bumps this signal so the board's ◇ marks and standings follow without a remount.
  useEffect(() => {
    if (!refreshSignal) return;
    void loadPendingProposals();
    void loadStandings();
  }, [refreshSignal, loadPendingProposals, loadStandings]);
  const untreatedByDel = useMemo(() => {
    const m = new Map<number, Proposal[]>();
    const byRef = new Map(dels.map((d) => [d.ref.toUpperCase(), d.id]));
    const add = (delId: number, p: Proposal) => { if (!Number.isFinite(delId)) return; const list = m.get(delId) ?? []; list.push(p); m.set(delId, list); };
    for (const p of pendingBoardProposals) {
      const kindStr = p.kind as unknown as string;
      const payload = p.payload as Record<string, unknown>;
      if (kindStr === 'create_deliverable_item') { add(Number(payload.deliverable_id), p); continue; }
      if (kindStr === 'provisional' && payload.entity_type === 'deliverable') { add(Number(payload.entity_id), p); continue; }
      // Amendments carry a ref, not an id — the shared helper names the landing deliverable.
      const landing = proposalDeliverableRef(kindStr, payload);
      if (landing) {
        const delId = byRef.get(landing.toUpperCase());
        if (delId !== undefined) add(delId, p);
      }
    }
    return m;
  }, [pendingBoardProposals, dels]);
  /** The same `provisional` proposals, matched to a REQUIREMENT instead — the Satisfies list's ◇. */
  const untreatedByReq = useMemo(() => {
    const m = new Map<number, Proposal[]>();
    for (const p of pendingBoardProposals) {
      const payload = p.payload as Record<string, unknown>;
      if ((p.kind as unknown as string) !== 'provisional' || payload.entity_type !== 'requirement') continue;
      const reqId = Number(payload.entity_id);
      if (!Number.isFinite(reqId)) continue;
      const list = m.get(reqId) ?? [];
      list.push(p);
      m.set(reqId, list);
    }
    return m;
  }, [pendingBoardProposals]);

  /** The open benches, oldest first. The last `cap` show side by side; the rest fold into the rail. */
  const [openIds, setOpenIds] = useState<number[]>([]);
  const [cap, setCap] = useState(1);
  const [flashes, setFlashes] = useState<Record<number, number>>({});
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  /** Every OPEN question of the engagement — the pencil's fallback tether list. */
  const [openQs, setOpenQs] = useState<TetherQuestion[]>([]);
  /**
   * THE PENCIL LAYER'S SWITCH. `record` draws the dates the engagement is held to; `assume`
   * draws the plan — overridden cards at their pencilled dates, the record left behind as a
   * faint slot. The operator's last choice sticks; where they have not chosen, the board opens
   * on the plan IF there is one, because an assumption nobody can see is a trap.
   */
  const [mode, setMode] = useState<ViewMode>(() => readViewMode() ?? 'record');
  const modeChosen = useRef<boolean>(readViewMode() !== null);
  const [dockH, setDockH] = useState(0);
  // The planner drawer is fixed at the page level (RegisterPage mounts it for the whole
  // deliverables tab) and knows nothing of benches — the board PUBLISHES the offset its dock
  // demands as a root CSS variable, and the drawer's own rules read it with an 18px fallback.
  useEffect(() => {
    const root = document.documentElement;
    if (openIds.length) root.style.setProperty('--planner-bottom', `${dockH + 34}px`);
    else root.style.removeProperty('--planner-bottom');
    return () => { root.style.removeProperty('--planner-bottom'); };
  }, [openIds.length, dockH]);
  const [live, setLive] = useState('');
  /** The board may break out of the page's 1180px and use the whole screen — persisted. */
  const [wideView, setWideView] = useState<boolean>(() => {
    try { return localStorage.getItem('signet.rw.wide') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('signet.rw.wide', wideView ? '1' : '0'); } catch { /* ignore */ } }, [wideView]);
  const didInitScroll = useRef(false);
  const liveT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const say = useCallback((msg: string) => {
    setLive(msg);
    if (liveT.current) clearTimeout(liveT.current);
    liveT.current = setTimeout(() => setLive(''), 4000);
  }, []);

  /** What # can reference in the benches' editors: deliverables, requirements, documents. */
  const boardRefOptions = useMemo<RefOption[]>(() => [
    ...dels.map((d) => ({ ref: d.ref, label: d.name })),
    ...rows.map((r) => ({ ref: r.ref, label: (r.statement ?? '').replace(/\s+/g, ' ').slice(0, 80) })),
    ...docs.map((d) => ({ ref: d.ref, label: d.original_filename })),
  ], [dels, rows, docs]);

  useEffect(() => {
    documentsApi.list(engagementId).then(setDocs).catch(() => setDocs([]));
    registerApi.openQuestions(engagementId)
      .then((qs) => setOpenQs(qs.filter((q) => q.status === 'open').map((q) => ({ id: q.id, ref: q.ref, question: q.question }))))
      .catch(() => setOpenQs([]));
  }, [engagementId]);

  // ── the pencil layer ────────────────────────────────────────────────────────
  const anyOverride = useMemo(() => dels.some((r) => (r.overrides ?? []).length > 0), [dels]);
  // Default (never a preference the operator expressed): show the plan when there IS one.
  useEffect(() => {
    if (modeChosen.current || !anyOverride) return;
    setMode('assume');
  }, [anyOverride]);
  const setViewMode = useCallback((next: ViewMode) => {
    modeChosen.current = true;
    setMode(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* private mode */ }
  }, []);
  /** The date the board DRAWS: the assumption in `assume`, the record everywhere else. */
  const effDate = useCallback(
    (r: Deliverable, field: 'due_date' | 'start_date'): string | null => {
      const rec = field === 'due_date' ? r.due_date : r.start_date;
      if (mode !== 'assume') return rec;
      return overrideOf(r.overrides, field)?.value ?? rec;
    },
    [mode],
  );

  // ── the calendar ──
  const dated = useMemo(() => dels.filter((r) => effDate(r, 'due_date')), [dels, effDate]);
  // The range is NEVER only what is occupied: it always includes the current month, so an
  // emptied month stays scrollable and drop-able. Both pictures (record and assumptions)
  // share one origin and one span — an assumption that moved a card must not push the
  // record's own slot off the edge of the grid.
  const base: YM = useMemo(() => {
    const n = new Date();
    const today: YM = { y: n.getFullYear(), m: n.getMonth() + 1 };
    const firsts = dated.flatMap((r) => [
      r.start_date ?? r.due_date,
      effDate(r, 'start_date') ?? effDate(r, 'due_date'),
    ].filter(Boolean) as string[]).sort();
    if (!firsts.length) return today;
    const p = parseIso(firsts[0]);
    return p.y * 12 + p.m < today.y * 12 + today.m ? { y: p.y, m: p.m } : today;
  }, [dated, effDate]);
  const monthCount = useMemo(() => {
    // Both pictures share one calendar, so flipping the switch never re-scales the grid under
    // the operator: the span covers the record AND the plan.
    const lasts = dated.flatMap((r) => [r.due_date, effDate(r, 'due_date')].filter(Boolean) as string[]).sort();
    if (!lasts.length) return 6;
    const p = parseIso(lasts[lasts.length - 1]);
    // No 6-month floor when the data is tight — a one-week project must not be smushed
    // across half a year. The fit below gives short spans their width automatically.
    return Math.max(3, (p.y - base.y) * 12 + (p.m - base.m) + 2);
  }, [dated, base, effDate]);
  const months: YM[] = useMemo(
    () => Array.from({ length: monthCount }, (_, i) => ({ y: base.y + Math.floor((base.m - 1 + i) / 12), m: ((base.m - 1 + i) % 12) + 1 })),
    [base, monthCount],
  );
  const totalWeeks = monthCount * WEEKS_PER_MONTH;
  // THE CALENDAR FITS THE SCREEN: week width is measured off the live scrollport (resize,
  // rotation, the ⤢ breakout — all of it re-scales the weeks), never picked from a zoom
  // tier. The grid fills its sheet exactly whenever the span allows; only when the fit
  // would drop below the readable floor does it overflow into the compass-panned scroll.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollW, setScrollW] = useState(1100);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setScrollW(el.clientWidth || 1100);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const weekPx = fitWeekPx(scrollW, 36 /* .rw's own 18px padding, both sides */, totalWeeks);

  const weekOf = useCallback((s: string): number => {
    const p = parseIso(s);
    const mi = (p.y - base.y) * 12 + (p.m - base.m);
    const q = Math.min(WEEKS_PER_MONTH - 1, Math.floor(((p.d - 1) / dim(p.y, p.m)) * WEEKS_PER_MONTH));
    return Math.min(totalWeeks - 1, Math.max(0, mi * WEEKS_PER_MONTH + q));
  }, [base, totalWeeks]);
  // A WEEK'S DATES ARE THE EXACT INVERSE OF weekOf, not an approximation of it. weekOf
  // files day d under week q while q·dim/4 ≤ d−1 < (q+1)·dim/4, so week q runs from day
  // ceil(q·dim/4)+1 through day ceil((q+1)·dim/4) — the weeks tile the month, no day
  // belongs to two of them and none to neither. Math.round here once minted dates OUTSIDE
  // their own week (a 31-day month's week 4 “began” on the 24th, a day weekOf files under
  // week 3): a card whose start sat on such a boundary drew one week left of where it was
  // dropped, and pulling its edge a week right wrote the very same date again — a stubborn
  // card no gesture could move.
  const weekEndIso = useCallback((w: number): string => {
    const mi = Math.floor(w / WEEKS_PER_MONTH), q = w % WEEKS_PER_MONTH;
    const ym = months[Math.min(mi, months.length - 1)];
    return iso(ym.y, ym.m, Math.ceil(((q + 1) * dim(ym.y, ym.m)) / WEEKS_PER_MONTH));
  }, [months]);
  const weekStartIso = useCallback((w: number): string => {
    const mi = Math.floor(w / WEEKS_PER_MONTH), q = w % WEEKS_PER_MONTH;
    const ym = months[Math.min(mi, months.length - 1)];
    return iso(ym.y, ym.m, Math.ceil((q * dim(ym.y, ym.m)) / WEEKS_PER_MONTH) + 1);
  }, [months]);

  /**
   * THE STILL BOARD's session memory: the lane every card was LAST drawn in. A card with a
   * stored roadmap_lane is pinned by the record; one without keeps the lane this map
   * remembers, so nothing re-packs under the operator mid-session — an auto-packed card gets
   * first-fit exactly once (on first sight) and then holds still until dragged, which pins it
   * for good.
   */
  const stickyLanes = useRef(new Map<number, number>());
  const cards = useMemo(() => {
    const span = (due: string, start: string | null) => {
      const ew = weekOf(due);
      return { sw: start ? Math.min(weekOf(start), ew) : Math.max(0, ew - 3), ew };
    };
    const list = dated.map((r) => {
      const drawn = span(effDate(r, 'due_date')!, effDate(r, 'start_date'));
      const override = mode === 'assume' ? overrideOf(r.overrides, 'due_date') ?? overrideOf(r.overrides, 'start_date') : null;
      // The record's own position, kept when the card is standing somewhere else: a faint slot
      // says what the engagement is still held to while the plan floats above it.
      const rec = override && r.due_date ? span(r.due_date, r.start_date) : null;
      const slot = rec && (rec.sw !== drawn.sw || rec.ew !== drawn.ew) ? rec : null;
      return {
        r, override, id: r.id, sw: drawn.sw, ew: drawn.ew, slot, name: r.name, ghost: !!override,
        pin: r.roadmap_lane ?? stickyLanes.current.get(r.id) ?? null,
      };
    }).sort((a, b) => a.sw - b.sw || a.ew - b.ew);
    // Lane packing and compactness live in roadmapLayout.ts — pure and unit-tested.
    // Pinned cards keep their lane verbatim; only never-placed ones pack.
    const laid = layoutCards(list, weekPx, totalWeeks);
    for (const l of laid) stickyLanes.current.set(l.card.r.id, l.lane);
    return laid.map((l) => ({
      r: l.card.r, sw: l.card.sw, ew: l.card.ew, lane: l.lane, ghost: l.card.override,
      slot: l.card.slot, compact: l.compact, refInside: l.refInside,
    }));
  }, [dated, weekOf, effDate, mode, weekPx, totalWeeks]);
  // Always one empty lane below the deepest card: the room to arrange INTO. Dropping there
  // pins the card and the next empty lane appears beneath it.
  const laneCount = Math.max(3, cards.reduce((n, c) => Math.max(n, c.lane + 1), 0) + 1);
  const undatedRows = useMemo(() => dels.filter((r) => !effDate(r, 'due_date')), [dels, effDate]);

  const order = useMemo(() => cards.map((c) => c.r.id).concat(undatedRows.map((r) => r.id)), [cards, undatedRows]);
  const rowOf = useCallback((reqId: number): RegisterRow | null => rows.find((r) => r.id === reqId) ?? null, [rows]);

  const patchDeliverable = useCallback(async (id: number, body: Parameters<typeof deliverablesApi.update>[1], word?: string) => {
    try {
      const fresh = await deliverablesApi.update(id, body);
      setDels((rs) => rs.map((r) => (r.id === id ? { ...r, ...fresh } : r)));
      if (word) say(word);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The change was refused.'));
      onReload();
    }
  }, [toast, onReload, say, t]);

  /** Re-read ONE deliverable (its assumptions ride along on the payload) without a page reload. */
  const reloadDeliverable = useCallback(async (id: number) => {
    try {
      const fresh = await deliverablesApi.get(id);
      setDels((rs) => rs.map((r) => (r.id === id ? { ...r, ...fresh } : r)));
    } catch { onReload(); }
  }, [onReload]);

  /**
   * A DRAG IN THE ASSUMPTIONS VIEW EDITS THE ASSUMPTION, NOT THE RECORD. This is the natural
   * way to pencil: flip the switch, drag DEL-01 into October, and the engagement is still held
   * to September until the call says otherwise.
   *
   * The tether is not guessed: an assumption already on this card keeps ITS question, otherwise
   * the deliverable's own open question is used (the server computed which ones name its
   * requirements). With no candidate the drag is REFUSED and says so — a date hanging on
   * nothing can never be settled, and silently tethering it to an unrelated question would be
   * a lie the audit trail would then repeat.
   */
  const pencilDrag = useCallback(async (id: number, next: Partial<Record<'due_date' | 'start_date', string>>) => {
    const d = dels.find((r) => r.id === id);
    if (!d) return;
    // A field that is ALREADY pencilled keeps its own question; a fresh one borrows the card's
    // other assumption, and failing that the deliverable's own open question.
    const fallback = (d.overrides ?? [])[0] ?? null;
    const candidate = (d.open_questions ?? [])[0] ?? null;
    const plan = (Object.entries(next) as ['due_date' | 'start_date', string][]).map(([field, value]) => {
      const own = overrideOf(d.overrides, field) ?? fallback;
      return {
        field, value,
        qId: own?.open_question_id ?? candidate?.id ?? null,
        qRef: own?.question_ref ?? candidate?.ref ?? null,
      };
    });
    if (plan.some((x) => x.qId === null)) {
      toast('err', t('{ref} has no open question to hang an assumption on — tether one in the workbench first.', { ref: d.ref }));
      return;
    }
    try {
      for (const x of plan) {
        await registerApi.setOverride(engagementId, {
          entity_type: 'deliverable', entity_id: id, field: x.field, value: x.value, open_question_id: x.qId!,
        });
      }
      await reloadDeliverable(id);
      const shown = plan.find((x) => x.field === 'due_date') ?? plan[0];
      say(t('{ref} planned for {date} — assumption, waiting on {q}', { ref: d.ref, date: fmtDate(shown.value), q: shown.qRef ?? '' }));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The assumption was refused.'));
      await reloadDeliverable(id);
    }
  }, [dels, engagementId, reloadDeliverable, say, toast, t, fmtDate]);

  // ── the dock: open beside, glide the map so the card stays in view, the oldest gives way ──
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLDivElement | null>(null);

  const glideTo = useCallback((id: number) => {
    requestAnimationFrame(() => {
      const scroll = scrollRef.current, card = gridRef.current?.querySelector<HTMLElement>(`[data-id="${id}"]`);
      if (scroll && card) scroll.scrollTo({ left: Math.max(0, card.offsetLeft - 30), behavior: 'smooth' });
      // vertically: if the card would sit under the dock, the page glides up just enough
      if (card) {
        const rect = card.getBoundingClientRect();
        const floor = window.innerHeight - dockHeight() - 24;
        if (rect.bottom > floor) window.scrollBy({ top: rect.bottom - floor, behavior: 'smooth' });
      }
    });
  }, []);

  const openRef = useRef<number[]>([]);
  openRef.current = openIds;

  const refOf = useCallback((id: number) => dels.find((r) => r.id === id)?.ref ?? `#${id}`, [dels]);
  /** Bring a bench to the front of the dock (the end of the list); fold whatever no longer fits. */
  const surface = useCallback((id: number) => {
    const ids = openRef.current;
    const prevShown = ids.slice(-dockCapacity(ids.length));
    const next = [...ids.filter((x) => x !== id), id];
    const nextCap = dockCapacity(next.length);
    const nowFolded = prevShown.filter((x) => !next.slice(-nextCap).includes(x));
    if (nowFolded.length) say(t('{refs} folded to the right — the dock shows {n} at this width', { refs: nowFolded.map(refOf).join(', '), n: nextCap }));
    setOpenIds(next);
    setCap(nextCap);
    glideTo(id);
  }, [glideTo, say, refOf, t]);
  const openWb = useCallback((id: number) => {
    const ids = openRef.current;
    if (ids.includes(id) && ids.slice(-cap).includes(id)) {
      // already in view: it glows to say so
      setFlashes((f) => ({ ...f, [id]: (f[id] ?? 0) + 1 }));
      glideTo(id);
      return;
    }
    surface(id);
  }, [cap, glideTo, surface]);
  const closeWb = useCallback((id: number) => setOpenIds((ids) => ids.filter((x) => x !== id)), []);
  /** ‹ › inside a bench: the neighbouring deliverable not already open in another bench. */
  const stepTarget = useCallback((fromId: number, dir: -1 | 1): number | null => {
    let at = order.indexOf(fromId);
    if (at < 0) return null;
    for (at += dir; at >= 0 && at < order.length; at += dir) if (!openRef.current.includes(order[at])) return order[at];
    return null;
  }, [order]);
  /** ‹ › inside a bench: the same slot shows that neighbour. */
  const stepWb = useCallback((fromId: number, dir: -1 | 1) => {
    const cand = stepTarget(fromId, dir);
    if (cand === null) return;
    setOpenIds((ids) => ids.map((x) => (x === fromId ? cand : x)));
    glideTo(cand);
  }, [stepTarget, glideTo]);

  // Esc closes the newest bench; a narrower window FOLDS benches into the rail (never kills
  // them), a wider one unfolds them again.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      setOpenIds((ids) => ids.slice(0, -1));
    };
    const onResize = () => setCap(dockCapacity(openRef.current.length));
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); };
  }, []);
  useEffect(() => { setCap(dockCapacity(openIds.length)); }, [openIds.length]);
  // the page grows by the dock's height so the whole map can be scrolled up above the benches
  useEffect(() => {
    const el = dockRef.current;
    if (!el || !openIds.length) { setDockH(0); return; }
    const ro = new ResizeObserver(() => setDockH(el.getBoundingClientRect().height));
    ro.observe(el);
    setDockH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [openIds.length]);
  // benches that lost their deliverable (a reload dropped it) close themselves
  useEffect(() => {
    setOpenIds((ids) => { const keep = ids.filter((id) => dels.some((r) => r.id === id)); return keep.length === ids.length ? ids : keep; });
  }, [dels]);

  // ── drag a card / pull an edge / GRAB THE GRID to pan ──
  const drag = useRef<{
    kind: 'card' | 'pan';
    id?: number; el?: HTMLElement; mode?: 'move' | 'l' | 'r';
    x0: number; sw0?: number; ew0?: number; w0?: number; colW?: number; sl0?: number; moved: boolean;
    /** Where the pointer last was — the release decides between the grid and the tray with it. */
    lastX?: number; lastY?: number;
    /** The card's lane at grab time, the grid's row tops measured then, and the lane in flight. */
    lane0?: number; rowTops?: number[]; gridTop?: number; laneTarget?: number;
    /** The applied X translation — stored, never parsed back out of a transform string. */
    txCur?: number;
  } | null>(null);

  /** The lane under a viewport Y, measured off the LIVE grid rows (they are auto-height). */
  const laneAtY = useCallback((clientY: number): number => {
    const grid = gridRef.current;
    if (!grid) return 0;
    const style = getComputedStyle(grid);
    const gap = parseFloat(style.rowGap) || 0;
    const rows = style.gridTemplateRows.split(' ').map((s) => parseFloat(s) || 0);
    let top = grid.getBoundingClientRect().top;
    let lane = rows.length - 1;
    for (let i = 0; i < rows.length; i += 1) {
      if (clientY < top) { lane = i - 1; break; }
      top += rows[i] + gap;
    }
    return Math.min(rows.length - 1, Math.max(0, lane));
  }, []);

  // ── the tray also CATCHES: a dated card dragged onto it goes back to undated ──
  // The inverse of the tray drop above, under the same contract: the record view undates the
  // record; the assumptions view rubs the date assumptions out (and refuses to touch a card
  // the RECORD dates — that undate belongs to the record view). While a card is in flight the
  // tray renders even when empty, so there is always somewhere to pull back to.
  const trayRef = useRef<HTMLDivElement | null>(null);
  const [cardDragging, setCardDragging] = useState(false);
  const [trayHot, setTrayHot] = useState(false);
  const overTrayAt = useCallback((x: number, y: number): boolean => {
    const r = trayRef.current?.getBoundingClientRect();
    return !!r && y >= r.top && y <= r.bottom && x >= r.left && x <= r.right;
  }, []);
  const pullBackToTray = useCallback((id: number) => {
    const d = dels.find((r) => r.id === id);
    if (!d) return;
    if (mode === 'assume') {
      if (d.due_date) {
        toast('err', t('{ref} is dated on the record — switch to “As recorded” to pull it back.', { ref: d.ref }));
        return;
      }
      const os = [overrideOf(d.overrides, 'due_date'), overrideOf(d.overrides, 'start_date')]
        .filter((o): o is WorkingOverride => o !== null);
      void (async () => {
        try { for (const o of os) await registerApi.revertOverride(o.id, engagementId); }
        catch { /* the reload below states whatever survived */ }
        await reloadDeliverable(id);
        say(t('{ref} → undated — back in the tray', { ref: d.ref }));
      })();
      return;
    }
    setDels((rs) => rs.map((r) => (r.id === id ? { ...r, due_date: null, start_date: null } : r)));
    void patchDeliverable(id, { due_date: null, start_date: null }, t('{ref} → undated — back in the tray', { ref: d.ref }));
  }, [dels, mode, toast, t, engagementId, reloadDeliverable, say, patchDeliverable]);

  const onPointerDown = (ev: React.PointerEvent) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>('.rw-card');
    if (!el) {
      // empty grid: grab and shift the timeline laterally
      const scroll = scrollRef.current; if (!scroll) return;
      drag.current = { kind: 'pan', x0: ev.clientX, sl0: scroll.scrollLeft, moved: false };
      scroll.style.scrollBehavior = 'auto';
      gridRef.current?.classList.add('panning');
      (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
      return;
    }
    if (!gridRef.current) return;
    const id = Number(el.dataset.id);
    const card = cards.find((c) => c.r.id === id); if (!card) return;
    const h = (ev.target as HTMLElement).closest<HTMLElement>('.rw-hnd');
    // Row tops, measured once at grab time: the vertical snap targets them while the card
    // is in flight, and the release reads the lane off the pointer, not off a re-pack.
    const gridStyle = getComputedStyle(gridRef.current);
    const rowGap = parseFloat(gridStyle.rowGap) || 0;
    const rowTops: number[] = [];
    let accTop = 0;
    for (const hpx of gridStyle.gridTemplateRows.split(' ').map((s) => parseFloat(s) || 0)) {
      rowTops.push(accTop);
      accTop += hpx + rowGap;
    }
    drag.current = {
      kind: 'card', id, el, mode: (h?.dataset.h as 'l' | 'r' | undefined) ?? 'move', x0: ev.clientX,
      sw0: card.sw, ew0: card.ew, w0: el.getBoundingClientRect().width,
      colW: gridRef.current.getBoundingClientRect().width / totalWeeks, moved: false,
      lane0: card.lane, laneTarget: card.lane, rowTops,
      gridTop: gridRef.current.getBoundingClientRect().top,
    };
    el.setPointerCapture(ev.pointerId);
  };
  const onPointerMove = (ev: React.PointerEvent) => {
    const d = drag.current; if (!d) return;
    const dx = ev.clientX - d.x0;
    if (Math.abs(dx) > 5 && !d.moved) { d.moved = true; if (d.kind === 'card') d.el!.classList.add('dragging'); }
    if (!d.moved) return;
    if (d.kind === 'pan') {
      const scroll = scrollRef.current; if (scroll) scroll.scrollLeft = (d.sl0 ?? 0) - dx;
      return;
    }
    d.lastX = ev.clientX; d.lastY = ev.clientY;
    if (d.mode === 'move') {
      // While a whole card is in flight, the tray offers itself as the way back to undated.
      setCardDragging(true);
      const hot = overTrayAt(ev.clientX, ev.clientY);
      setTrayHot((cur) => (cur === hot ? cur : hot));
    }
    const el = d.el!, colW = d.colW!, span = d.ew0! - d.sw0!;
    if (d.mode === 'move') {
      let tx = Math.min(Math.max(dx, -d.sw0! * colW), (totalWeeks - 1 - span - d.sw0!) * colW);
      const snap = Math.round(tx / colW) * colW;
      if (Math.abs(tx - snap) < SNAP_PX) tx = snap;
      d.txCur = tx;
      // Vertically the card snaps lane to lane — the pointer picks the row, the card sits ON
      // it (never between two), and the release pins exactly what the operator sees.
      const rt = d.rowTops ?? [];
      let lane = d.lane0 ?? 0;
      if (rt.length) {
        const y = ev.clientY - (d.gridTop ?? 0);
        lane = rt.length - 1;
        for (let i = 0; i < rt.length; i += 1) if (y < rt[i]) { lane = Math.max(0, i - 1); break; }
      }
      d.laneTarget = lane;
      const ty = (rt[lane] ?? 0) - (rt[d.lane0 ?? 0] ?? 0);
      el.style.transform = `translate(${tx}px, ${ty}px)`;
    } else if (d.mode === 'r') {
      let w = Math.max(colW * 0.9, d.w0! + dx);
      const snap = Math.round(w / colW) * colW;
      if (Math.abs(w - snap) < SNAP_PX) w = snap;
      el.style.width = `${w}px`;
    } else {
      let tx = Math.min(Math.max(dx, -d.sw0! * colW), span * colW);
      const snap = Math.round(tx / colW) * colW;
      if (Math.abs(tx - snap) < SNAP_PX) tx = snap;
      d.txCur = tx;
      el.style.transform = `translateX(${tx}px)`;
      el.style.width = `${d.w0! - tx}px`;
    }
  };
  const onPointerUp = () => {
    const d = drag.current; if (!d) return;
    drag.current = null;
    setCardDragging(false);
    setTrayHot(false);
    if (d.kind === 'pan') {
      gridRef.current?.classList.remove('panning');
      const scroll = scrollRef.current; if (scroll) scroll.style.scrollBehavior = '';
      return;
    }
    const el = d.el!, colW = d.colW!;
    el.classList.remove('dragging');
    // Stored during the move, never parsed back out of the transform string — a 2-axis
    // `translate(x, y)` cannot be recovered with a strip-and-parseFloat.
    const tx = d.txCur ?? 0;
    const w = el.style.width ? parseFloat(el.style.width) : d.w0!;
    el.style.transform = ''; el.style.width = '';
    if (!d.moved) { openWb(d.id!); return; }
    // Released over the tray: the card goes back to undated instead of to a week.
    if (d.mode === 'move' && d.lastX !== undefined && overTrayAt(d.lastX, d.lastY!)) {
      pullBackToTray(d.id!);
      return;
    }
    const span = d.ew0! - d.sw0!;
    let sw = d.sw0!, ew = d.ew0!;
    if (d.mode === 'move') { sw = Math.min(Math.max(0, d.sw0! + Math.round(tx / colW)), totalWeeks - 1 - span); ew = sw + span; }
    else if (d.mode === 'r') { ew = Math.min(totalWeeks - 1, Math.max(d.sw0!, d.sw0! + Math.round(w / colW) - 1)); }
    else { sw = Math.min(Math.max(0, d.sw0! + Math.round(tx / colW)), d.ew0!); }
    // THE STILL BOARD: any completed gesture PINS the card at the lane it stands in — a moved
    // card at the lane the pointer chose, a stretched one right where it already was — so no
    // later re-pack can move it out from under the operator's arrangement.
    const lane = d.mode === 'move' ? (d.laneTarget ?? d.lane0 ?? 0) : (d.lane0 ?? 0);
    const laneChanged = lane !== (d.lane0 ?? 0);
    const datesMoved = sw !== d.sw0 || ew !== d.ew0;
    if (!datesMoved && !laneChanged) return;
    const due = weekEndIso(ew), start = weekStartIso(sw);
    const ref = dels.find((r) => r.id === d.id)?.ref ?? '';
    stickyLanes.current.set(d.id!, lane);
    if (mode === 'assume') {
      // Arrangement is not an assumption: the lane pins on the record even from the plan
      // view — only the DATES go through the pencil layer.
      if (laneChanged) {
        setDels((rs) => rs.map((r) => (r.id === d.id ? { ...r, roadmap_lane: lane } : r)));
        void patchDeliverable(d.id!, { roadmap_lane: lane }, datesMoved ? undefined : t('{ref} pinned to its lane', { ref }));
      }
      if (!datesMoved) return;
      // Only what the gesture actually moved becomes an assumption — pulling the right edge
      // pencils a due date and says nothing about the start.
      const next: Partial<Record<'due_date' | 'start_date', string>> =
        d.mode === 'r' ? { due_date: due } : d.mode === 'l' ? { start_date: start } : { due_date: due, start_date: start };
      void pencilDrag(d.id!, next);
      return;
    }
    setDels((rs) => rs.map((r) => (r.id === d.id ? { ...r, due_date: due, start_date: start, roadmap_lane: lane } : r)));
    void patchDeliverable(
      d.id!,
      { due_date: due, start_date: start, roadmap_lane: lane },
      datesMoved ? t('{ref} → due {date}', { ref, date: fmtDate(due) }) : t('{ref} pinned to its lane', { ref }),
    );
  };

  // ── the tray drops onto the grid: an undated deliverable is DATED by where it lands ──
  // Native drag-and-drop, not the grid's pointer machinery: a tray chip is not a card yet, it
  // has no span to stretch and no lane — it only needs a week. The drop obeys the board's one
  // drag contract: the record view writes the record, the assumptions view writes a pencilled
  // assumption (and refuses without a tether, exactly like re-dragging a pencilled card).
  /** The week column a dragged tray chip hovers over — the drop-target highlight. */
  const [dropWeek, setDropWeek] = useState<number | null>(null);
  const trayWeekAt = useCallback((clientX: number): number => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    return Math.min(totalWeeks - 1, Math.max(0, Math.floor(((clientX - rect.left) / rect.width) * totalWeeks)));
  }, [totalWeeks]);
  const onTrayDragOver = useCallback((e: React.DragEvent) => {
    // Own MIME type, not text/plain: a stray text selection dragged across the grid must not
    // light up a drop target it cannot mean.
    if (!e.dataTransfer.types.includes('application/x-signet-deliverable')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const w = trayWeekAt(e.clientX);
    setDropWeek((cur) => (cur === w ? cur : w));
  }, [trayWeekAt]);
  const onTrayDragLeave = useCallback((e: React.DragEvent) => {
    // dragleave also fires when the pointer enters a CHILD of the grid — only a real exit clears.
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setDropWeek(null);
  }, []);
  const onTrayDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropWeek(null);
    const id = Number(e.dataTransfer.getData('application/x-signet-deliverable'));
    const d = undatedRows.find((r) => r.id === id);
    if (!d) return;
    const due = weekEndIso(trayWeekAt(e.clientX));
    // The drop position picks the LANE as well as the week — the card lands pinned exactly
    // where it was put, still-board rules from its first second on the grid.
    const lane = laneAtY(e.clientY);
    stickyLanes.current.set(id, lane);
    if (mode === 'assume') {
      void patchDeliverable(id, { roadmap_lane: lane });
      void pencilDrag(id, { due_date: due });
      return;
    }
    // Only the due date is written: the card then draws its default span, and pulling its
    // edges afterwards is how a start date is stated — same as any other fresh card.
    setDels((rs) => rs.map((r) => (r.id === id ? { ...r, due_date: due, roadmap_lane: lane } : r)));
    void patchDeliverable(id, { due_date: due, roadmap_lane: lane }, t('{ref} → due {date}', { ref: d.ref, date: fmtDate(due) }));
  }, [undatedRows, weekEndIso, trayWeekAt, laneAtY, mode, pencilDrag, patchDeliverable, t, fmtDate]);

  // The default view starts AT the earliest deliverable — months before it exist (drop
  // targets, reachable by scrolling back or via the compass) but are not shown by default.
  useEffect(() => {
    if (didInitScroll.current || !cards.length) return;
    const scroll = scrollRef.current;
    if (!scroll) return;
    didInitScroll.current = true;
    const minSw = Math.min(...cards.map((c) => c.sw));
    const monthStartWeek = minSw - (minSw % WEEKS_PER_MONTH);
    requestAnimationFrame(() => {
      const prev = scroll.style.scrollBehavior;
      scroll.style.scrollBehavior = 'auto';
      scroll.scrollLeft = (monthStartWeek / totalWeeks) * scroll.scrollWidth;
      scroll.style.scrollBehavior = prev;
      syncCompass();
    });
  }, [cards, totalWeeks]);

  // ── the compass band: week ticks, long month lines, a draggable viewport window ──
  const compassRef = useRef<HTMLDivElement | null>(null);
  const compassWin = useRef<HTMLDivElement | null>(null);
  const syncCompass = useCallback(() => {
    const scroll = scrollRef.current, win = compassWin.current;
    if (!scroll || !win || scroll.scrollWidth === 0) return;
    // A pan instrument with nothing to pan is noise: when the calendar fits, the band goes.
    compassRef.current?.classList.toggle('off', scroll.scrollWidth <= scroll.clientWidth + 2);
    win.style.left = `${(scroll.scrollLeft / scroll.scrollWidth) * 100}%`;
    win.style.width = `${(scroll.clientWidth / scroll.scrollWidth) * 100}%`;
  }, []);
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(syncCompass); };
    scroll.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    syncCompass();
    return () => { scroll.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); cancelAnimationFrame(raf); };
  }, [syncCompass, wideView, monthCount, weekPx]);
  const compassDrag = useRef<{ grabX: number; startLeft: number } | null>(null);
  const compassJump = (clientX: number, centre: boolean) => {
    const band = compassRef.current, scroll = scrollRef.current;
    if (!band || !scroll) return;
    const r = band.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const target = frac * scroll.scrollWidth - (centre ? scroll.clientWidth / 2 : 0);
    const prev = scroll.style.scrollBehavior;
    scroll.style.scrollBehavior = 'auto';
    scroll.scrollLeft = Math.min(scroll.scrollWidth - scroll.clientWidth, Math.max(0, target));
    scroll.style.scrollBehavior = prev;
  };
  const onCompassDown = (ev: React.PointerEvent) => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    if ((ev.target as HTMLElement).classList.contains('win')) {
      compassDrag.current = { grabX: ev.clientX, startLeft: scroll.scrollLeft };
    } else {
      compassJump(ev.clientX, true);
      compassDrag.current = { grabX: ev.clientX, startLeft: scroll.scrollLeft };
    }
  };
  const onCompassMove = (ev: React.PointerEvent) => {
    const d = compassDrag.current, band = compassRef.current, scroll = scrollRef.current;
    if (!d || !band || !scroll) return;
    const dx = ev.clientX - d.grabX;
    const prev = scroll.style.scrollBehavior;
    scroll.style.scrollBehavior = 'auto';
    scroll.scrollLeft = d.startLeft + (dx / band.getBoundingClientRect().width) * scroll.scrollWidth;
    scroll.style.scrollBehavior = prev;
  };
  const onCompassUp = () => { compassDrag.current = null; };

  const openSet = useMemo(() => new Set(openIds), [openIds]);
  const shownIds = useMemo(() => openIds.slice(-cap), [openIds, cap]);
  const foldedIds = useMemo(() => (openIds.length > cap ? openIds.slice(0, openIds.length - cap) : []), [openIds, cap]);

  return (
    <>
      <div className={`sheet sheet--strong rw-board${wideView ? ' rw-board--wide' : ''}`}>
        <div className="rw-head">
          <span className="u-label">{t('The roadmap')}</span>
          <span className="rw-viewsw" role="group" aria-label={t('Which roadmap you are looking at')}>
            {(['record', 'assume'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={`rw-viewbtn${mode === m ? ' on' : ''}`}
                aria-pressed={mode === m}
                title={m === 'record'
                  ? t('The dates the engagement is held to — a drag writes them')
                  : t('The plan: assumptions drawn where they are pencilled, the record left as a faint slot — a drag writes the assumption')}
                onClick={() => setViewMode(m)}
              >{m === 'record' ? t('As recorded') : t('With assumptions')}</button>
            ))}
          </span>
          <span className="rw-hint">{mode === 'assume'
            ? t('drag a card = re-plan the assumption · the record does not move · click = work out')
            : t('drag a card = re-date · pull an edge = stretch · grab the grid = pan · click = work out (click another = beside it)')}</span>
          <span className="rw-live voice-figure">{live}</span>
          <button
            type="button"
            className="rw-widebtn"
            title={wideView ? t('Back to the page width') : t('Expand the roadmap to the full screen')}
            aria-pressed={wideView}
            onClick={() => setWideView((v) => !v)}
          >{wideView ? '⤡' : '⤢'}</button>
        </div>
        <div className="rw-scroll" ref={scrollRef}>
          <div className="rw" style={{ minWidth: totalWeeks * weekPx + 36 }}>
            <div className="rw-months" style={{ gridTemplateColumns: `repeat(${monthCount}, 1fr)` }}>
              {months.map((ym) => (
                <div className="rw-month" key={`${ym.y}-${ym.m}`}>
                  <span className="u-label">
                    {t(MONTHS[ym.m - 1])}
                    <span className="y">{String(ym.y).slice(2)}</span>
                  </span>
                </div>
              ))}
            </div>
            <div
              className="rw-grid"
              ref={gridRef}
              style={{
                gridTemplateColumns: `repeat(${totalWeeks}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${laneCount}, minmax(108px, auto))`,
                backgroundSize: `${100 / totalWeeks}% 100%, ${100 / monthCount}% 100%`,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onDragOver={onTrayDragOver}
              onDragLeave={onTrayDragLeave}
              onDrop={onTrayDrop}
            >
              {/* the week a dragged tray chip would land in */}
              {dropWeek !== null ? (
                <div
                  className="rw-dropcol"
                  aria-hidden="true"
                  style={{ gridColumn: `${dropWeek + 1}/${dropWeek + 2}`, gridRow: `1 / ${laneCount + 1}` }}
                />
              ) : null}
              {/* where the record still stands, while the plan floats above it */}
              {cards.filter((c) => c.slot).map((c) => (
                <div
                  key={`slot-${c.r.id}`}
                  className="rw-slot"
                  aria-hidden="true"
                  title={t('{ref} on the record: {date}', { ref: c.r.ref, date: fmtDate(c.r.due_date) })}
                  style={{ gridColumn: `${c.slot!.sw + 1}/${c.slot!.ew + 2}`, gridRow: c.lane + 1 }}
                >
                  <span className="src-ref">{c.r.ref}</span>
                  <span className="voice-figure">{fmtDate(c.r.due_date)}</span>
                </div>
              ))}
              {cards.map((c) => (
                <div
                  key={c.r.id}
                  data-id={c.r.id}
                  className={`rw-card${openSet.has(c.r.id) ? ' sel' : ''}${c.ghost ? ' rw-card--ghost' : ''}${c.compact ? ' rw-card--bar' : ''}`}
                  style={{ gridColumn: `${c.sw + 1}/${c.ew + 2}`, gridRow: c.lane + 1 }}
                  title={c.compact
                    ? `${c.r.ref} · ${c.r.name} — ${fmtDate(effDate(c.r, 'due_date'))}`
                      + (c.ghost ? `\n${t('Assumption — waiting on {ref}: {question}', { ref: c.ghost.question_ref ?? '—', question: c.ghost.question })}` : '')
                    : undefined}
                >
                  <span className="rw-hnd l" data-h="l" /><span className="rw-hnd r" data-h="r" />
                  {c.compact ? (
                    // THE SHORT CARD: the exact span kept (its edges ARE the dates, and the
                    // handles stay on them), the same height as its neighbours, the name
                    // WRAPPED INSIDE and clamped — nothing floats outside the border where
                    // an adjacent card could paint over it. Date, full name and the
                    // assumption's question live in the tooltip.
                    <>
                      <div className="top">
                        {c.refInside ? <span className="src-ref">{c.r.ref}</span> : null}
                        {c.ghost ? <span className="att att--proposed" aria-hidden="true" /> : null}
                      </div>
                      <h3>{c.r.name}</h3>
                    </>
                  ) : (
                    <>
                      <div className="top">
                        <span className="src-ref">{c.r.ref}</span>
                        <span className="due">{fmtDate(effDate(c.r, 'due_date'))}</span>
                      </div>
                      <h3>{c.r.name}</h3>
                      <CardMarks del={c.r} untreated={untreatedByDel.get(c.r.id) ?? EMPTY_PROPOSALS} />
                      {c.ghost ? (
                        <div
                          className="rw-annahme"
                          title={t('Assumption — waiting on {ref}: {question}', { ref: c.ghost.question_ref ?? '—', question: c.ghost.question })}
                        >
                          <span className="att att--proposed" aria-hidden="true" />
                          <span className="tag-unheld">{t('Assumption')}</span>
                          <span className="src-ref">{c.ghost.question_ref ?? ''}</span>
                        </div>
                      ) : null}
                      <div className="reqs">
                        {c.r.requirements.slice(0, 6).map((q) => <span className="rq" key={q.id}>{q.ref.slice(4)}</span>)}
                        {c.r.requirements.length > 6 ? <span className="rq">+{c.r.requirements.length - 6}</span> : null}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          className="rw-compass"
          ref={compassRef}
          onPointerDown={onCompassDown}
          onPointerMove={onCompassMove}
          onPointerUp={onCompassUp}
          onPointerCancel={onCompassUp}
          aria-hidden="true"
        >
          {Array.from({ length: totalWeeks + 1 }, (_, w) => (
            <span key={w} className={`ck${w % WEEKS_PER_MONTH === 0 ? ' mo' : ''}`} style={{ left: `${(w / totalWeeks) * 100}%` }} />
          ))}
          <div className="win" ref={compassWin} />
        </div>
        {undatedRows.length || cardDragging ? (
          <div className={`rw-tray${trayHot ? ' hot' : ''}`} ref={trayRef}>
            <span className="u-label">{t('undated')}</span>
            {cardDragging ? <span className="q">{t('drop a card here to undate it')}</span> : null}
            {undatedRows.map((r) => (
              // The same card the grid draws — a month's width of it, with no date to show —
              // so what waits in the tray already looks like what it becomes on the board.
              <button
                key={r.id}
                type="button"
                className={`rw-card rw-card--tray${openSet.has(r.id) ? ' sel' : ''}`}
                style={{ width: weekPx * WEEKS_PER_MONTH }}
                draggable
                title={t('Drag onto the roadmap to date it · click to work it out')}
                onDragStart={(e) => { e.dataTransfer.setData('application/x-signet-deliverable', String(r.id)); e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => setDropWeek(null)}
                onClick={() => openWb(r.id)}
              >
                <div className="top">
                  <span className="src-ref">{r.ref}</span>
                </div>
                <h3>{r.name}</h3>
                <CardMarks del={r} untreated={untreatedByDel.get(r.id) ?? EMPTY_PROPOSALS} />
                <div className="reqs">
                  {r.requirements.slice(0, 6).map((q) => <span className="rq" key={q.id}>{q.ref.slice(4)}</span>)}
                  {r.requirements.length > 6 ? <span className="rq">+{r.requirements.length - 6}</span> : null}
                </div>
              </button>
            ))}
          </div>
        ) : null}
        <div className="rw-note">
          {t('Faint lines are weeks, stronger lines are months.')}{' '}
          {nodes(t(mode === 'assume'
            ? 'A drag writes an assumption over {start} / {due} — never the row itself.'
            : 'A drag writes {start} / {due} and an audit row.'), {
            start: <span className="src-ref">start_date</span>, due: <span className="src-ref">due_date</span>,
          })}{' '}
          {t('Undated deliverables wait in the tray until you date them — drag one onto the grid.')}{' '}
          {mode === 'assume'
            ? t('You are looking at the plan: a dashed card is an assumption, the faint slot behind it is what the engagement is held to. Settling its question on the client call reverts it or adopts it.')
            : anyOverride
              ? t('Assumptions are pencilled on this roadmap — switch to “With assumptions” to see them.')
              : t('An assumption is planned in a workbench: pick a date, hang it on the open question that has to settle first.')}
        </div>
      </div>

      {/* the page grows by the dock's height: the map can always be scrolled up above the benches */}
      {openIds.length ? <div className="rw-dock-space" aria-hidden="true" style={{ height: dockH + 20 }} /> : null}

      {/* ── the dock: the open benches side by side, the ones that do not fit folded at the right ── */}
      {openIds.length ? (
        <div className="rw-dock" ref={dockRef} role="region" aria-label={t('Workbenches')}>
          {shownIds.map((id) => {
            const del = dels.find((r) => r.id === id);
            if (!del) return null;
            return (
              <Workbench
                key={id}
                engagementId={engagementId}
                del={del}
                docs={docs}
                rowOf={rowOf}
                onOpenSpan={onOpenSpan}
                onReload={onReload}
                onClose={() => closeWb(id)}
                onStep={(dir) => stepWb(id, dir)}
                hasPrev={stepTarget(id, -1) !== null}
                hasNext={stepTarget(id, 1) !== null}
                patchDeliverable={patchDeliverable}
                openQuestions={openQs}
                allDels={dels}
                onOpenBench={openWb}
                refOptions={boardRefOptions}
                onOverrideChange={(delId) => void reloadDeliverable(delId)}
                requirementStandings={requirementStandings}
                untreatedProposals={untreatedByDel.get(id) ?? EMPTY_PROPOSALS}
                untreatedByReq={untreatedByReq}
                onGoToQuestion={goToQuestion}
                onStandingsChange={() => void loadStandings()}
                onProposalChange={() => void loadPendingProposals()}
                flash={flashes[id] ?? 0}
              />
            );
          })}
          {foldedIds.length ? (
            <div className="rw-fold" aria-label={t('Folded benches')}>
              {[...foldedIds].reverse().map((id) => (
                <div className="rw-foldtab" key={id}>
                  <button type="button" className="x" title={t('Close {ref}', { ref: refOf(id) })} onClick={() => closeWb(id)}>×</button>
                  <button
                    type="button"
                    className="lbl"
                    title={t('{ref} · {name} — unfold', { ref: refOf(id), name: dels.find((r) => r.id === id)?.name ?? '' })}
                    onClick={() => surface(id)}
                  >
                    <span className="src-ref">{refOf(id)}</span>
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
